import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronDown, Cpu, DollarSign, Flame, Globe, Clock, Calendar, CalendarDays, TrendingUp, TrendingDown, Scale, Coins, Activity, Shuffle, AlertTriangle, Share2, Check as CheckIcon, Lock } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import AIAnalysisModal from '../components/AIAnalysisModal';
import { useSubscription } from '../hooks/useSubscription';
import ProBadge from '../components/ui/ProBadge';
import { apiGet, apiPost } from '../lib/apiClient';


const getEAIdentityKey = (ea) => `${ea.account_id || 0}:${ea.magic || 0}:${String(ea.ea_name || '').trim().toLowerCase()}`;
const STATUS_COLORS = {
  EXCELLENT: { bg: '#00ff8820', text: '#00ff88' },
  GOOD: { bg: '#378ADD20', text: '#378ADD' },
  AVERAGE: { bg: '#f59e0b20', text: '#f59e0b' },
  POOR: { bg: '#ff475720', text: '#ff4757' },
};

const EA_LIVE_THRESHOLD_MS = 5 * 60 * 1000;
const EA_IDLE_THRESHOLD_MS = 30 * 60 * 1000;

function parseSyncTime(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatSyncAge(value) {
  const date = parseSyncTime(value);
  if (!date) return 'Never synced';
  const diffMs = Math.max(0, Date.now() - date.getTime());
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getEaActivityStatus(lastUpdated) {
  const date = parseSyncTime(lastUpdated);
  if (!date) return { label: 'Offline', color: '#ff4757', bg: '#ff475720', border: '#ff475755', sub: 'Never synced' };
  const ageMs = Math.max(0, Date.now() - date.getTime());
  if (ageMs <= EA_LIVE_THRESHOLD_MS) return { label: 'Live', color: '#00ff88', bg: '#00ff8820', border: '#00ff8855', sub: formatSyncAge(lastUpdated) };
  if (ageMs <= EA_IDLE_THRESHOLD_MS) return { label: 'Idle', color: '#f59e0b', bg: '#f59e0b20', border: '#f59e0b55', sub: formatSyncAge(lastUpdated) };
  return { label: 'Offline', color: '#ff4757', bg: '#ff475720', border: '#ff475755', sub: formatSyncAge(lastUpdated) };
}

function newestTimestamp(values) {
  return values
    .map(parseSyncTime)
    .filter(Boolean)
    .sort((a, b) => b.getTime() - a.getTime())[0]?.toISOString() || null;
}

export default function MyEAs() {
  const navigate = useNavigate();
  const { hasProAccess, isFree } = useSubscription();
  const [eaStats, setEaStats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [accountType, setAccountType] = useState('all');
  const [loadError, setLoadError] = useState('');
  const [sortBy, setSortBy] = useState('net_profit');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [checkedEAs, setCheckedEAs] = useState({});
  const [selectedEA, setSelectedEA] = useState(null);
  const [eaDetail, setEaDetail] = useState(null);
  const [aiAnalysisEA, setAiAnalysisEA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    const params = new URLSearchParams();
    if (selectedAccount !== 'all') params.set('account_id', selectedAccount);
    else if (accountType !== 'all') params.set('type_filter', accountType);
    const query = params.toString() ? `?${params.toString()}` : '';

    Promise.allSettled([
      apiGet(`/api/ea-stats${query}`),
      apiGet('/api/accounts'),
    ]).then(([easResult, accountsResult]) => {
      if (cancelled) return;
      if (easResult.status === 'fulfilled') {
        const eas = easResult.value || [];
        setEaStats(eas);
        const init = {};
        eas.forEach(ea => { init[getEAIdentityKey(ea)] = true; });
        setCheckedEAs(init);
      }
      if (accountsResult.status === 'fulfilled') setAccounts(accountsResult.value || []);
      if (easResult.status === 'rejected' && accountsResult.status === 'fulfilled') {
        setLoadError('EA performance could not be loaded. Your accounts are still available.');
      } else if (easResult.status === 'rejected' || accountsResult.status === 'rejected') {
        setLoadError('Some account data could not be loaded.');
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [selectedAccount, accountType, refreshKey]);

  const handleEAClick = async (ea) => {
    setSelectedEA(ea);
    setEaDetail(null);

    try {
      if (ea._grouped && ea._magics && ea._magics.length > 1) {
        // Multiple instances: fetch each exact account+magic scope so detail data matches the cards/tabs.
        const results = await Promise.all(
          ea._magics.map(m => apiGet(`/api/ea-stats/${m.magic}/detail?account_id=${m.account_id}&ea_name=${encodeURIComponent(m.ea_name || ea.ea_name || "")}`).catch(() => null))
        );
        const valid = results.filter(r => r);
        const combinedTrades = valid.flatMap(r => r.recent_trades || []);
        const combinedOpen = valid.flatMap(r => r.open_trades || []);
        const totalClosed = valid.reduce((s, r) => s + (r.total_closed_trades || 0), 0);
        const combinedCashflows = valid.reduce((acc, r) => {
          const cf = r.cashflows || {};
          acc.total_deposits += cf.total_deposits || 0;
          acc.total_withdrawals += cf.total_withdrawals || 0;
          acc.deposit_count += cf.deposit_count || 0;
          acc.withdrawal_count += cf.withdrawal_count || 0;
          acc.cashflow_count += cf.cashflow_count || 0;
          return acc;
        }, { total_deposits: 0, total_withdrawals: 0, deposit_count: 0, withdrawal_count: 0, cashflow_count: 0 });
        combinedCashflows.net_cashflow = combinedCashflows.total_deposits - combinedCashflows.total_withdrawals;
        const bestEq = valid.reduce((best, r) => (r.equity_curve||[]).length > best.length ? r.equity_curve : best, []);
        setEaDetail({
          stats: ea,
          recent_trades: combinedTrades,
          open_trades: combinedOpen,
          total_closed_trades: totalClosed,
          equity_curve: bestEq,
          dd_chart: valid[0]?.dd_chart || [],
          float_chart: valid[0]?.float_chart || [],
          hourly_performance: valid[0]?.hourly_performance || [],
          daily_performance: valid[0]?.daily_performance || [],
          monthly_heatmap: valid[0]?.monthly_heatmap || [],
          daily_heatmap: valid[0]?.daily_heatmap || [],
          cashflows: combinedCashflows,
          trading_costs: valid[0]?.trading_costs || {},
          session_performance: valid[0]?.session_performance || {},
          symbol_breakdown: valid[0]?.symbol_breakdown || [],
          monte_carlo: valid[0]?.monte_carlo || null,
          monthly_analytics: null,
        });
      } else {
        // Single instance: fetch with account_id to get only this account's data.
        const aid = ea.account_id ? `?account_id=${ea.account_id}` : '';
        const detail = await apiGet(`/api/ea-stats/${ea.magic}/detail${aid}`);
        setEaDetail(detail);
      }
    } catch (e) {
      // Show error state so user isn't stuck on loading
      setEaDetail({ stats: ea, recent_trades: [], open_trades: [], total_closed_trades: 0 });
    }
  };

  // Sort EAs
  const sortedEAs = useMemo(() => {
    let filtered = eaStats.filter(ea => checkedEAs[getEAIdentityKey(ea)]);
    // Same magic number can belong to different EAs. Group only by a real normalized EA name.
    const normalizeEAName = (name) => {
      if (!name) return '';
      let n = name.replace(/\[.*?\.(ecn|pc|raw|pro|std|micro|cent|stp)\]/gi, '[SYMBOL]');
      n = n.replace(/\[T\d+\/S\d+\]/gi, '');
      const trimmed = n.trim().toLowerCase();
      return /^ea_\d+$/.test(trimmed) ? '' : trimmed;
    };

    const grouped = {};
    filtered.forEach(ea => {
      const normName = normalizeEAName(ea.ea_name);
      const identityKey = getEAIdentityKey(ea);
      let foundGroup = null;
      if (normName) {
        for (const [key, members] of Object.entries(grouped)) {
          if (members.some(m => normalizeEAName(m.ea_name) === normName)) { foundGroup = key; break; }
        }
      }
      if (foundGroup) grouped[foundGroup].push(ea);
      else grouped[normName || identityKey] = [ea];
    });

    const primaryEAs = Object.entries(grouped).map(([, instances]) => {
      if (instances.length === 1) return instances[0];
      const primary = { ...instances[0] };
      const names = instances.map(m => m.ea_name || `EA_${m.magic}`);
      primary.ea_name = names.reduce((shortest, n) => n.length < shortest.length ? n : shortest, names[0]);
      primary._grouped = true;
      primary._magics = instances;
      primary.net_profit = instances.reduce((s, m) => s + m.net_profit, 0);
      primary.total_trades = instances.reduce((s, m) => s + m.total_trades, 0);
      primary.winning_trades = instances.reduce((s, m) => s + m.winning_trades, 0);
      primary.losing_trades = instances.reduce((s, m) => s + m.losing_trades, 0);
      primary.open_trades_count = instances.reduce((s, m) => s + (m.open_trades_count || 0), 0);
      primary.last_updated = newestTimestamp(instances.map(m => m.last_updated));
      primary.win_rate = primary.total_trades > 0 ? parseFloat((primary.winning_trades / primary.total_trades * 100).toFixed(1)) : 0;
      primary.max_drawdown = Math.max(...instances.map(m => Number(m.max_drawdown || 0)), 0);
      primary.max_drawdown_pct = Math.max(...instances.map(m => Number(m.max_drawdown_pct || 0)), 0);
      primary.avg_drawdown = parseFloat((instances.reduce((s, m) => s + Number(m.avg_drawdown || 0), 0) / instances.length).toFixed(2));
      primary.avg_drawdown_pct = parseFloat((instances.reduce((s, m) => s + Number(m.avg_drawdown_pct || 0), 0) / instances.length).toFixed(2));
      const grossProfit = instances.reduce((s, m) => s + (m.winning_trades > 0 ? m.net_profit > 0 ? m.net_profit * (m.winning_trades / m.total_trades) : 0 : 0), 0) || instances.filter(m => m.net_profit > 0).reduce((s, m) => s + m.net_profit, 0);
      const grossLoss = Math.abs(instances.filter(m => m.net_profit < 0).reduce((s, m) => s + m.net_profit, 0)) || 1;
      primary.profit_factor = parseFloat((grossProfit / grossLoss).toFixed(2)) || parseFloat((primary.winning_trades / Math.max(primary.losing_trades, 1)).toFixed(2));
      primary.expectancy = primary.total_trades > 0 ? parseFloat((primary.net_profit / primary.total_trades).toFixed(2)) : 0;
      primary.status = primary.win_rate > 80 && primary.profit_factor > 2 ? 'EXCELLENT'
        : primary.win_rate > 65 && primary.profit_factor > 1.5 ? 'GOOD'
        : primary.win_rate > 50 ? 'AVERAGE'
        : 'POOR';
      return primary;
    });

    const sorted = [...primaryEAs].sort((a, b) => {
      if (sortBy === 'name_az') return String(a.ea_name || '').localeCompare(String(b.ea_name || ''));
      if (sortBy === 'name_za') return String(b.ea_name || '').localeCompare(String(a.ea_name || ''));
      return (b[sortBy] || 0) - (a[sortBy] || 0);
    });
    return sorted;
  }, [eaStats, checkedEAs, sortBy]);
  const visibleAccounts = useMemo(() => {
    if (accountType === 'real') return accounts.filter(a => !a.is_demo);
    if (accountType === 'demo') return accounts.filter(a => a.is_demo);
    return accounts;
  }, [accounts, accountType]);
  const scopedAccounts = useMemo(() => selectedAccount === 'all'
    ? visibleAccounts
    : accounts.filter(a => String(a.id) === String(selectedAccount)), [accounts, visibleAccounts, selectedAccount]);
  const totalBalance = scopedAccounts.reduce((s, a) => s + (a.balance || 0), 0);

  if (loading) {
    return <div className="page-wrap"><p style={{ color: 'var(--text-secondary, #8b949e)' }}>Loading EAs...</p></div>;
  }

  return (
    <div className="page-wrap">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', gap: '12px', flexWrap: 'wrap' }}>
        <h1 style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '22px', fontWeight: 800, margin: 0 }}>EA Performance</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: '1px solid var(--border-color, #30363d)', borderRadius: 7, overflow: 'hidden' }}>
            {[['all', 'All'], ['real', 'Real'], ['demo', 'Demo']].map(([value, label]) => (
              <button key={value} onClick={() => { setAccountType(value); setSelectedAccount('all'); }} style={{ padding: '8px 12px', border: 0, cursor: 'pointer', fontSize: 11, fontWeight: 700, background: accountType === value ? '#00ff88' : 'var(--bg-main, #0d1117)', color: accountType === value ? 'var(--bg-main, #0d1117)' : 'var(--text-secondary, #8b949e)' }}>{label}</button>
            ))}
          </div>
          <select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #30363d)', borderRadius: '6px', padding: '8px 12px', color: 'var(--text-primary, #e6edf3)', fontSize: '12px', minWidth: '220px' }}>
            <option value="all">All {accountType === 'all' ? '' : accountType === 'real' ? 'Real ' : 'Demo '}Accounts ({visibleAccounts.length})</option>
            {visibleAccounts.map(a => <option key={a.id} value={a.id}>{a.broker || 'Account'} #{a.account_number} {a.is_demo ? '[DEMO]' : '[REAL]'}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <IconStat label="Total Profit" value={`$${eaStats.reduce((s,e) => s + (e.net_profit || 0), 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color={eaStats.reduce((s,e) => s + (e.net_profit || 0), 0) >= 0 ? '#00ff88' : '#ff4757'} />
        <IconStat label="Total Trades" value={eaStats.reduce((s,e) => s + (e.total_trades || 0), 0)} color="var(--text-primary, #e6edf3)" />
        <IconStat label="Max DD" value={`$${Math.max(...eaStats.map(e => e.max_drawdown || 0), 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color="#f59e0b" />
        <IconStat label="Avg Win Rate" value={`${eaStats.length > 0 ? (eaStats.reduce((s,e) => s + (e.win_rate || 0), 0) / eaStats.length).toFixed(1) : 0}%`} color="#22d3ee" />
        <IconStat label="Active EAs" value={eaStats.length} color="#a855f7" />
      </div>

      <LiveCapitalOverview eaStats={eaStats} accounts={scopedAccounts} totalBalance={totalBalance} />
      {loadError && (
        <div style={{ marginBottom: 16, padding: '10px 12px', border: '1px solid #f59e0b55', borderRadius: 8, background: '#f59e0b12', color: '#f59e0b', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span><AlertTriangle size={14} style={{ display: 'inline', marginRight: 7, verticalAlign: '-2px' }} />{loadError}</span>
          <button type="button" onClick={() => setRefreshKey((key) => key + 1)} style={{ border: '1px solid #f59e0b88', borderRadius: 5, padding: '5px 10px', color: '#f59e0b', background: 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>Retry</button>
        </div>
      )}
      {/* EA Performance Filter */}
      <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '13px', fontWeight: 600, margin: 0 }}>EA Performance Filter</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { const all = {}; eaStats.forEach(e => { all[getEAIdentityKey(e)] = true; }); setCheckedEAs(all); }}
              style={{ padding: '4px 12px', background: '#00ff88', border: 'none', borderRadius: '4px', color: 'var(--bg-main, #0d1117)', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Select All</button>
            <button onClick={() => setCheckedEAs({})}
              style={{ padding: '4px 12px', background: '#ff4757', border: 'none', borderRadius: '4px', color: '#fff', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Deselect All</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {(() => {
            // Group filter items the same way as cards: same normalized real EA name, not same magic.
            const normalizeN = (name) => {
              if (!name) return '';
              const n = name.replace(/\[.*?\.(ecn|pc|raw|pro|std|micro|cent|stp)\]/gi, '[SYMBOL]').replace(/\[T\d+\/S\d+\]/gi, '').trim().toLowerCase();
              return /^ea_\d+$/.test(n) ? '' : n;
            };
            const filterGroups = {};
            eaStats.forEach(ea => {
              const normName = normalizeN(ea.ea_name);
              const identityKey = getEAIdentityKey(ea);
              let found = null;
              if (normName) {
                for (const [key, members] of Object.entries(filterGroups)) {
                  if (members.some(m => normalizeN(m.ea_name) === normName)) { found = key; break; }
                }
              }
              if (found) { filterGroups[found].push(ea); }
              else { filterGroups[normName || identityKey] = [ea]; }
            });            return Object.values(filterGroups).map(group => {
              const allKeys = group.map(g => getEAIdentityKey(g));
              const isChecked = allKeys.every(k => checkedEAs[k]);
              const totalProfit = group.reduce((s, g) => s + g.net_profit, 0);
              const displayName = group.reduce((shortest, g) => (g.ea_name || '').length < (shortest || '').length ? g.ea_name : shortest, group[0].ea_name);
              return (
                <label key={allKeys.join('-')} onClick={() => setCheckedEAs(prev => {
                  const next = { ...prev };
                  const newVal = !isChecked;
                  allKeys.forEach(k => { next[k] = newVal; });
                  return next;
                })}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', background: isChecked ? 'var(--bg-main, #0d1117)' : '#0d111780', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>
                  <input type="checkbox" checked={isChecked} readOnly style={{ accentColor: '#00ff88' }} />
                  <span style={{ color: 'var(--text-primary, #e6edf3)' }}>{displayName}</span>
                  <span style={{ color: totalProfit >= 0 ? '#00ff88' : '#ff4757', fontWeight: 600 }}>${totalProfit.toFixed(0)}</span>
                </label>
              );
            });
          })()}
        </div>
      </div>

      {/* Sort + Status Filter + Title */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px' }}>Sort by:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #30363d)', borderRadius: '6px', padding: '4px 10px', color: 'var(--text-primary, #e6edf3)', fontSize: '12px' }}>
              <option value="net_profit">Net Profit</option>
              <option value="win_rate">Win Rate</option>
              <option value="profit_factor">Profit Factor</option>
              <option value="total_trades">Total Trades</option>
              <option value="max_drawdown">Max Drawdown ($)</option>
              <option value="name_az">EA Name (A-Z)</option>
              <option value="name_za">EA Name (Z-A)</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['ALL', 'EXCELLENT', 'GOOD', 'AVERAGE', 'POOR'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '9px', fontWeight: 700, cursor: 'pointer', border: 'none',
                  background: statusFilter === s ? (s === 'EXCELLENT' ? '#00ff8820' : s === 'GOOD' ? '#378ADD20' : s === 'AVERAGE' ? '#f59e0b20' : s === 'POOR' ? '#ff475720' : 'var(--border-color, #21262d)') : 'var(--bg-main, #0d1117)',
                  color: statusFilter === s ? (s === 'EXCELLENT' ? '#00ff88' : s === 'GOOD' ? '#378ADD' : s === 'AVERAGE' ? '#f59e0b' : s === 'POOR' ? '#ff4757' : 'var(--text-secondary, #8b949e)') : 'var(--text-dim, #484f58)',
                }}>
                {s === 'ALL' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>
        <h2 style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '14px', fontWeight: 600, margin: 0 }}>Expert Advisors Performance</h2>
      </div>

      {/* EA Cards Grid â€” responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedEAs.filter(ea => statusFilter === 'ALL' || ea.status === statusFilter).map(ea => (
          <EACard key={`${ea.account_id}-${ea.magic}`} ea={ea} onClick={() => handleEAClick(ea)} onStrategyClick={handleEAClick} allEAs={eaStats} totalBalance={totalBalance} accounts={accounts} />
        ))}
      </div>

      {sortedEAs.filter(ea => statusFilter === 'ALL' || ea.status === statusFilter).length === 0 && (
        <p style={{ color: 'var(--text-dim, #484f58)', textAlign: 'center', padding: '40px', fontSize: '13px' }}>No EAs match the current filter.</p>
      )}

      {/* EA Detail Modal */}
      {selectedEA && eaDetail && (
        <EADetailModal ea={selectedEA} detail={eaDetail} onClose={() => { setSelectedEA(null); setEaDetail(null); }} totalBalance={totalBalance} onNavigate={navigate} accounts={accounts} onAIAnalyze={(ea) => { setSelectedEA(null); setEaDetail(null); setAiAnalysisEA(ea); }} />
      )}

      {/* AI Analysis Modal */}
      {aiAnalysisEA && (
        <AIAnalysisModal ea={aiAnalysisEA} onClose={() => setAiAnalysisEA(null)} />
      )}
      {selectedEA && !eaDetail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setSelectedEA(null); }}>
          <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '16px', padding: '40px 60px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '16px', margin: '0 0 8px' }}>Loading Analysis...</p>
            <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px', margin: 0 }}>{selectedEA.ea_name || `EA_${selectedEA.magic}`}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function IconStat({ label, value, color }) {
  return (
    <div className="ui-card" style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '10px', padding: '14px 16px' }}>
      <p style={{ color, fontSize: '16px', fontWeight: 700, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{value}</p>
      <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', margin: '4px 0 0', letterSpacing: '0.5px', fontFamily: "'Space Grotesk', sans-serif" }}>{label}</p>
    </div>
  );
}

function EACard({ ea, onClick, onStrategyClick, allEAs, totalBalance, accounts = [] }) {
  const [showStrategies, setShowStrategies] = useState(false);
  const status = STATUS_COLORS[ea.status] || STATUS_COLORS.AVERAGE;
  const sparkData = useMemo(() => {
    // Generate approximate equity curve from available stats
    const points = 8;
    const totalProfit = ea.net_profit || 0;
    const maxDD = ea.max_drawdown || 0;
    const data = [];
    // Simple approximation: linear growth with a dip representing max drawdown
    for (let i = 0; i < points; i++) {
      const progress = i / (points - 1);
      let value = totalProfit * progress;
      // Add a dip around 40-60% of the way to represent drawdown
      if (progress > 0.3 && progress < 0.7) {
        const dipFactor = 1 - Math.sin((progress - 0.3) / 0.4 * Math.PI) * (maxDD / Math.max(Math.abs(totalProfit), 1)) * 0.5;
        value = value * Math.max(dipFactor, 0.3);
      }
      data.push({ v: value });
    }
    return data;
  }, [ea.net_profit, ea.max_drawdown]);

  const strategies = ea._grouped ? ea._magics.filter(m => m.magic !== ea.magic) : (allEAs || []).filter(e => e.ea_name === ea.ea_name && e.magic !== ea.magic);
  const allStrategies = ea._grouped ? ea._magics : [ea, ...strategies];
  const hasMultipleStrategies = allStrategies.length > 1;

  const profitColor = ea.net_profit >= 0 ? '#00ff88' : '#ff4757';
  const pctOfBalance = totalBalance > 0 ? ((ea.net_profit / totalBalance) * 100).toFixed(2) : '0.00';
  const ddPct = (ea.max_drawdown_pct || 0).toFixed(1);

  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '14px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden' }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-color, #30363d)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color, #21262d)'; e.currentTarget.style.transform = 'translateY(0)'; }}>

      {/* Top accent line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${profitColor}, ${profitColor}80)` }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
        <div>
          <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '16px', fontWeight: 700, margin: 0, letterSpacing: '-0.3px' }}>{ea.ea_name}</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', margin: '3px 0 0' }}>Magic: {ea.magic} | Click for details</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          {(() => {
            const acct = accounts.find(a => a.id === ea.account_id);
            const platform = acct?.platform || 'MT5';
            return <span style={{ fontSize: '9px', fontWeight: 700, padding: '3px 6px', borderRadius: '4px', background: '#378ADD15', color: '#378ADD', border: '1px solid #378ADD30' }}>{platform}</span>;
          })()}
          <span style={{ fontSize: '9px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px', background: status.bg, color: status.text, letterSpacing: '0.3px' }}>{ea.status}</span>
        </div>
      </div>

      {/* LIVE + Profit row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0', padding: '8px 12px', background: 'var(--bg-main, #0d1117)', borderRadius: '8px', border: '1px solid var(--border-color, #21262d)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '9px', fontWeight: 700, padding: '3px 8px', borderRadius: '4px', background: '#00ff8815', color: '#00ff88', border: '1px solid #00ff8830' }}>LIVE</span>
          <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px' }}>{ea.open_trades_count || 0} open</span>
        </div>
        <span style={{ color: profitColor, fontSize: '18px', fontWeight: 800, letterSpacing: '-0.5px' }}>
          {ea.net_profit >= 0 ? '' : '-'}${Math.abs(ea.net_profit).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </span>
      </div>

      {/* Sparkline */}
      <div style={{ height: '50px', margin: '0 -4px 14px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkData}>
            <defs>
              <linearGradient id={`eaG${ea.magic}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={profitColor} stopOpacity={0.2} />
                <stop offset="100%" stopColor={profitColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="v" stroke={profitColor} fill={`url(#eaG${ea.magic})`} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Stats */}
      <div style={{ fontSize: '11px', borderTop: '1px solid var(--border-color, #21262d)', paddingTop: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
          <Stat2 k="Net Profit" v={ea.net_profit.toFixed(2)} vc={profitColor} />
          <Stat2 k="% of Balance" v={`${pctOfBalance}%`} vc={profitColor} />
          <Stat2 k="Total Trades" v={ea.total_trades} />
          <Stat2 k="Win Rate" v={`${ea.win_rate}%`} vc="#00ff88" />
          <Stat2 k="Profit Factor" v={ea.profit_factor} />
          <Stat2 k="Expectancy" v={`$${ea.expectancy}`} vc={(ea.expectancy || 0) >= 0 ? '#00ff88' : '#ff4757'} />
          <Stat2 k="Days Active" v={ea.days_active || 0} />
          <Stat2 k="Risk/Reward" v={ea.risk_reward || 0} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '14px' }}>
        <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid rgba(255,71,87,0.18)', borderRadius: '8px', padding: '10px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: 0 }}>Max DD</p>
          <p style={{ color: '#ff4757', fontSize: '14px', fontWeight: 800, margin: '4px 0 0' }}>${(ea.max_drawdown || 0).toFixed(2)}</p>
          <p style={{ color: '#ff4757', fontSize: '10px', margin: 0 }}>({ddPct}%)</p>
        </div>
        <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '10px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: 0 }}>Avg DD</p>
          <p style={{ color: '#f59e0b', fontSize: '14px', fontWeight: 800, margin: '4px 0 0' }}>${(ea.avg_drawdown || 0).toFixed(2)}</p>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: 0 }}>({(ea.avg_drawdown_pct || 0).toFixed(1)}%)</p>
        </div>
      </div>

      {hasMultipleStrategies && (
        <div style={{ marginTop: '14px' }} onClick={e => e.stopPropagation()}>
          <button onClick={() => setShowStrategies(!showStrategies)} style={{ width: '100%', background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', color: 'var(--text-secondary, #8b949e)', padding: '8px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            {showStrategies ? 'Hide' : 'View'} Strategies ({allStrategies.length})
          </button>
          {showStrategies && (
            <div style={{ marginTop: '8px', display: 'grid', gap: '8px' }}>
              {allStrategies.map(st => (
                <div key={`${st.account_id}-${st.magic}`} onClick={e => { e.stopPropagation(); onStrategyClick(st); }} style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '10px 12px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', gap: '8px' }}>
                    <span style={{ color: '#a855f7', fontSize: '12px', fontWeight: 700 }}>Magic {st.magic}</span>
                    <span style={{ fontSize: '8px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', background: st.status === 'EXCELLENT' ? '#00ff8820' : st.status === 'GOOD' ? '#378ADD20' : st.status === 'POOR' ? '#ff475720' : '#f59e0b20', color: st.status === 'EXCELLENT' ? '#00ff88' : st.status === 'GOOD' ? '#378ADD' : st.status === 'POOR' ? '#ff4757' : '#f59e0b' }}>{st.status || 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '14px', fontSize: '11px', color: 'var(--text-secondary, #8b949e)', flexWrap: 'wrap' }}>
                    <span>P/L: <strong style={{ color: st.net_profit >= 0 ? '#00ff88' : '#ff4757' }}>{(st.net_profit || 0).toFixed(2)}</strong></span>
                    <span>WR: <strong>{st.win_rate}%</strong></span>
                    <span>PF: <strong>{st.profit_factor}</strong></span>
                    <span>Trades: <strong style={{ color: 'var(--text-primary, #e6edf3)' }}>{st.total_trades}</strong></span>
                  </div>
                  <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', margin: '4px 0 0' }}>Click for Detailed Analysis</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function Stat2({ k, v, vc }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: '#6b7280', fontSize: '10px' }}>{k}</span>
      <span style={{ color: vc || 'var(--text-primary, #e6edf3)', fontWeight: 700, fontSize: '11px' }}>{v}</span>
    </div>
  );
}

function EAMonthlyAnalyticsSection({ data }) {
  const months = data?.months || [];
  const perMonth = data?.per_month || {};
  const [selectedMonth, setSelectedMonth] = useState('');
  const colors = ['#00ff88', '#a855f7', '#38bdf8', '#f59e0b', '#ff4757', '#14b8a6', '#e879f9', '#60a5fa'];

  useEffect(() => {
    if (months.length > 0 && (!selectedMonth || !perMonth[selectedMonth])) {
      setSelectedMonth(months[months.length - 1].month);
    }
  }, [months, perMonth, selectedMonth]);

  if (!data || months.length === 0) return null;
  const monthData = perMonth[selectedMonth] || { risk_reward: [], popularity: [], holding_time: [] };
  const selectedMonthName = (() => {
    const d = new Date(`${selectedMonth}-01T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? selectedMonth : d.toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  })();
  const rrRows = (monthData.risk_reward || []).slice(0, 8).map(r => ({ ...r, reward_risk: r.reward_risk === 999 ? 10 : r.reward_risk }));
  const holdingRows = (monthData.holding_time || []).slice(0, 8);
  const fmtDuration = (seconds) => {
    if (!seconds) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(168,85,247,0.35)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div>
          <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '13px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}><CalendarDays size={14} /> Monthly Analytics</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', margin: '4px 0 0' }}>EA-specific gain, reward:risk, symbol mix, and holding time.</p>
        </div>
        <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ background: 'var(--bg-main, #0d1117)', color: 'var(--text-primary, #e6edf3)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '7px 10px', fontSize: '11px', colorScheme: 'dark' }}>
          {months.map(m => <option key={m.month} value={m.month}>{m.label}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
        <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px', fontWeight: 700, margin: '0 0 8px' }}>Monthly Net P/L</p>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #21262d)" />
              <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} angle={-35} textAnchor="end" height={52} />
              <YAxis tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
              <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Net P/L']} />
              <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                {months.map((entry, i) => <Cell key={i} fill={entry.profit >= 0 ? '#00ff88' : '#ff4757'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px', fontWeight: 700, margin: '0 0 8px' }}>Reward : Risk for {selectedMonthName}</p>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={rrRows} layout="vertical" margin={{ left: 70 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #21262d)" />
              <XAxis type="number" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
              <YAxis type="category" dataKey="symbol" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 10 }} width={70} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}R`, 'Reward:Risk']} />
              <Bar dataKey="reward_risk" fill="#38bdf8" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px', fontWeight: 700, margin: '0 0 8px' }}>Symbol Popularity for {selectedMonthName}</p>
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={(monthData.popularity || []).slice(0, 8)} dataKey="trades" nameKey="symbol" innerRadius={42} outerRadius={76} paddingAngle={2} label={({ symbol, percent }) => `${symbol} ${percent}%`}>
                {(monthData.popularity || []).slice(0, 8).map((entry, i) => <Cell key={entry.symbol} fill={colors[i % colors.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n, p) => [`${v} trades (${p.payload.percent}%)`, p.payload.symbol]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px', fontWeight: 700, margin: '0 0 8px' }}>Average Holding Time for {selectedMonthName}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', minHeight: '190px' }}>
            {holdingRows.length === 0 && <p style={{ color: 'var(--text-dim, #484f58)', textAlign: 'center', fontSize: '12px', padding: '50px 0' }}>No trades for this month.</p>}
            {holdingRows.map(row => {
              const max = Math.max(...holdingRows.map(r => Math.max(r.long_seconds || 0, r.short_seconds || 0, r.avg_seconds || 0)), 1);
              return (
                <div key={row.symbol}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '10px', fontWeight: 700 }}>{row.symbol}</span>
                    <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px' }}>Avg {fmtDuration(row.avg_seconds)}</span>
                  </div>
                  <div style={{ height: '14px', background: 'var(--border-color, #21262d)', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ height: '100%', width: `${Math.max(3, ((row.long_seconds || 0) / max) * 100)}%`, background: '#00cc6a' }} title={`Long ${fmtDuration(row.long_seconds)}`} />
                    <div style={{ height: '100%', width: `${Math.max(3, ((row.short_seconds || 0) / max) * 100)}%`, background: '#ff4757' }} title={`Short ${fmtDuration(row.short_seconds)}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
function EADetailModal({ ea, detail, onClose, totalBalance, onNavigate, accounts = [], onAIAnalyze }) {
  const { isFree } = useSubscription();
  const [instanceTab, setInstanceTab] = useState('combined');
  const [instanceDetail, setInstanceDetail] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [published, setPublished] = useState(null);
  const [publishFeedback, setPublishFeedback] = useState('');

  useEffect(() => {
    setPublished(null);
    setPublishFeedback('');
  }, [instanceTab]);

  // Determine instances (same EA across multiple accounts or multiple magics)
  const instances = useMemo(() => {
    if (ea._grouped && ea._magics && ea._magics.length > 1) {
      return ea._magics.map(m => {
        const acct = accounts.find(a => a.id === m.account_id);
        return { account_id: m.account_id, magic: m.magic, ea_name: m.ea_name, broker: acct?.broker || 'Unknown', account_number: acct?.account_number || m.account_id, is_demo: acct?.is_demo };
      });
    }
    return [];
  }, [ea, accounts]);

  const showTabs = instances.length > 1;

  // Fetch instance-specific detail when tab changes
  useEffect(() => {
    if (instanceTab === 'combined' || !showTabs) {
      setInstanceDetail(null);
      return;
    }
    // Tab value is "accountId_magic" for unique identification
    const [aid, magic] = instanceTab.split('_');
    apiGet(`/api/ea-stats/${magic || ea.magic}/detail?account_id=${aid}&ea_name=${encodeURIComponent(ea.ea_name || "")}`)
      .then(setInstanceDetail)
      .catch(() => setInstanceDetail(null));
  }, [instanceTab, ea.magic, showTabs]);

  // Use instance detail if a specific tab is selected, otherwise use combined
  const activeDetail = (instanceTab !== 'combined' && instanceDetail) ? instanceDetail : detail;
  const scopedAccountId = showTabs
    ? (instanceTab !== 'combined' ? parseInt(instanceTab.split('_')[0], 10) : null)
    : (ea.account_id || null);

  const s = activeDetail.stats || ea;
  const trades = activeDetail.recent_trades || [];
  const openTrades = activeDetail.open_trades || [];
  // Pre-computed data from backend (with client-side fallback for backward compatibility)
  const equityCurve = activeDetail.equity_curve && activeDetail.equity_curve.length > 0 ? activeDetail.equity_curve : (() => {
    let cum = 0;
    return trades.filter(t => t.close_time).sort((a,b) => new Date(a.close_time) - new Date(b.close_time)).map((t, i) => { cum += t.profit || 0; return { x: i + 1, equity: cum }; });
  })();
  const ddChart = activeDetail.dd_chart && activeDetail.dd_chart.length > 0 ? activeDetail.dd_chart : (() => {
    let cum = 0, peak = 0;
    return trades.filter(t => t.close_time).sort((a,b) => new Date(a.close_time) - new Date(b.close_time)).map(t => {
      cum += t.profit || 0; if (cum > peak) peak = cum;
      const dd = peak > 0 ? ((cum - peak) / peak * 100) : 0;
      const d = new Date(t.close_time);
      return { date: `${d.getMonth()+1}/${d.getDate()}`, value: Math.round(dd * 100) / 100 };
    });
  })();
  const floatChart = activeDetail.float_chart && activeDetail.float_chart.length > 0 ? activeDetail.float_chart : (() => {
    let cum = 0;
    return trades.filter(t => t.close_time).sort((a,b) => new Date(a.close_time) - new Date(b.close_time)).map(t => {
      cum += t.profit || 0;
      const d = new Date(t.close_time);
      return { date: `${d.getMonth()+1}/${d.getDate()}`, value: Math.round(cum * 100) / 100 };
    });
  })();
  // Helper: convert broker time string to NY hour
  // Broker time is EET (UTC+2 winter / UTC+3 summer = Europe/Helsinki)
  // We create a proper UTC timestamp from broker time, then convert to NY
  const toNYHour = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Parse the date parts manually to avoid local timezone interpretation
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return null;
      // Treat the parsed time as UTC+2 (EET base) by subtracting 2 hours to get UTC
      // Then use Intl to get NY time from that UTC moment
      const utcMs = d.getTime() - (d.getTimezoneOffset() * 60000); // get true UTC from local parse
      // Now we have UTC based on user's local TZ. But we need UTC based on broker TZ (UTC+2).
      // Broker hour offset: assume +2 (EET). Adjust: broker_time - 2h = UTC
      // Create a date that represents "this broker time as UTC+2"
      const parts = dateStr.replace('T', ' ').split(/[- :]/);
      const brokerDate = new Date(Date.UTC(+parts[0], +parts[1]-1, +parts[2], +parts[3]||0, +parts[4]||0, +parts[5]||0));
      // brokerDate is now the broker time treated as UTC. Subtract 2 hours to get real UTC (EET=UTC+2)
      // But EET has DST: UTC+2 in winter, UTC+3 in summer. Use a simple check:
      const month = +parts[1];
      const brokerOffset = (month >= 4 && month <= 10) ? 3 : 2; // EEST in Apr-Oct, EET otherwise
      const realUtcMs = brokerDate.getTime() - (brokerOffset * 3600000);
      const realUtc = new Date(realUtcMs);
      // Now convert UTC to NY using Intl
      const nyHourStr = realUtc.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false });
      return parseInt(nyHourStr) % 24;
    } catch (e) { return null; }
  };
  const toNYDay = (dateStr) => {
    if (!dateStr) return null;
    try {
      const parts = dateStr.replace('T', ' ').split(/[- :]/);
      const brokerDate = new Date(Date.UTC(+parts[0], +parts[1]-1, +parts[2], +parts[3]||0, +parts[4]||0, +parts[5]||0));
      const month = +parts[1];
      const brokerOffset = (month >= 4 && month <= 10) ? 3 : 2;
      const realUtcMs = brokerDate.getTime() - (brokerOffset * 3600000);
      const realUtc = new Date(realUtcMs);
      const nyDayStr = realUtc.toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short' });
      const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return map[nyDayStr] ?? 0;
    } catch (e) { return null; }
  };

  const hourlyPerf = activeDetail.hourly_performance && activeDetail.hourly_performance.length > 0 && activeDetail.hourly_performance.some(h => h.count > 0) ? activeDetail.hourly_performance : (() => {
    const hd = Array.from({length:24}, (_,h) => ({hour:h, profit:0, count:0}));
    trades.forEach(t => { if (t.open_time) { const h = toNYHour(t.open_time); if (h !== null) { hd[h].profit += t.profit||0; hd[h].count++; } } });
    return hd;
  })();
  const dailyPerf = activeDetail.daily_performance && activeDetail.daily_performance.length > 0 && activeDetail.daily_performance.some(d => d.count > 0) ? activeDetail.daily_performance : (() => {
    const dd = Array.from({length:7}, (_,i) => ({day: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i], profit:0, count:0, win_rate:0, wins:0}));
    trades.forEach(t => { if (t.open_time) { let d = toNYDay(t.open_time); if (d !== null) { d = d === 0 ? 6 : d - 1; dd[d].profit += t.profit||0; dd[d].count++; if ((t.profit||0) > 0) dd[d].wins++; } } });
    dd.forEach(d => { d.win_rate = d.count > 0 ? Math.round(d.wins/d.count*1000)/10 : 0; });
    return dd;
  })();
  const monthlyHeatmap = activeDetail.monthly_heatmap || [];
  const dailyHeatmap = activeDetail.daily_heatmap || [];
  const cashflows = activeDetail.cashflows || {};
  const costs = activeDetail.trading_costs && Object.keys(activeDetail.trading_costs).length > 0 ? activeDetail.trading_costs : (() => {
    const ts = trades.reduce((a, t) => a + (t.swap||0), 0);
    const tc = trades.reduce((a, t) => a + (t.commission||0), 0);
    return { total_swap: ts, total_commission: tc, total_costs: ts + tc, cost_per_trade: trades.length > 0 ? (ts+tc)/trades.length : 0 };
  })();
  const sessionPerf = activeDetail.session_performance && Object.keys(activeDetail.session_performance).length > 0 && Object.values(activeDetail.session_performance).some(s => s.count > 0) ? activeDetail.session_performance : (() => {
    // Session hours in NY time
    const sessions = { asian: {hours:[19,20,21,22,23,0,1,2,3], profit:0, count:0, win_rate:0, profit_factor:0}, london: {hours:[3,4,5,6,7,8,9,10,11], profit:0, count:0, win_rate:0, profit_factor:0}, new_york: {hours:[8,9,10,11,12,13,14,15,16], profit:0, count:0, win_rate:0, profit_factor:0} };
    Object.entries(sessions).forEach(([k, v]) => { let wins=0, gp=0, gl=0; trades.forEach(t => { if (t.open_time) { const h = toNYHour(t.open_time); if (h !== null && v.hours.includes(h)) { v.profit += t.profit||0; v.count++; if ((t.profit||0)>0) { wins++; gp += t.profit; } else { gl += Math.abs(t.profit||0); } } } }); v.win_rate = v.count > 0 ? Math.round(wins/v.count*1000)/10 : 0; v.profit_factor = gl > 0 ? Math.round(gp/gl*100)/100 : (gp > 0 ? 999.99 : 0); });
    return { asian: sessions.asian, london: sessions.london, new_york: sessions.new_york };
  })();
  // Helper: correct pips display for metals (existing data may have wrong values)
  const correctPips = (pips, symbol) => {
    if (!pips || !symbol) return pips || 0;
    const sym = symbol.toUpperCase();
    // If pips value is absurdly large for gold (>1000), it's likely raw points not pips
    if (sym.includes('XAU') && Math.abs(pips) > 1000) return pips / 10;
    if (sym.includes('XAG') && Math.abs(pips) > 10000) return pips / 10;
    return pips;
  };

  const totalClosedTrades = activeDetail.total_closed_trades || trades.length;
  const activityStatus = getEaActivityStatus(s.last_updated || ea.last_updated);
  const shareTarget = (() => {
    if (instanceTab && instanceTab !== 'combined') {
      const [aid, magic] = instanceTab.split('_');
      return { magic: parseInt(magic || ea.magic, 10), account_id: parseInt(aid, 10) };
    }
    return { magic: s.magic || ea.magic, account_id: ea._grouped ? null : (s.account_id || ea.account_id || null) };
  })();

  const togglePublishedProfile = async () => {
    if (!shareTarget.account_id) {
      setPublishFeedback('Select one account tab to publish.');
      return;
    }

    const shouldPublish = published !== true;
    if (shouldPublish && !window.confirm('Publish this account-specific EA profile in Explore? Anyone can view its verified metrics. Your account number and server are never exposed.')) return;

    setPublishLoading(true);
    setPublishFeedback('');
    try {
      const data = await apiPost('/api/ea-stats/share', {
        magic: shareTarget.magic,
        account_id: shareTarget.account_id,
        ea_name: s.ea_name || ea.ea_name,
        discoverable: shouldPublish,
      });
      setPublished(shouldPublish);
      if (shouldPublish) {
        const url = `${window.location.origin}${data.share_url}`;
        try { await navigator.clipboard.writeText(url); } catch (_) {}
        setPublishFeedback('Published in Explore. Profile link copied.');
      } else {
        setPublishFeedback('Removed from Explore. Private share link still works.');
      }
    } catch (error) {
      setPublishFeedback(error?.message || 'Could not update public profile.');
    } finally {
      setPublishLoading(false);
    }
  };

  return (
    <div className="ea-modal-clean" style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div id="ea-detail-panel" style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '16px', maxWidth: '850px', width: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color, #21262d)', position: 'sticky', top: 0, background: 'var(--bg-main, #0d1117)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexWrap: 'wrap' }}>
            <h2 style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '18px', fontWeight: 700, margin: 0 }}>{s.ea_name || ea.ea_name} - Detailed Analysis</h2>
            <div title={`Last data: ${activityStatus.sub}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 9px', borderRadius: '999px', background: activityStatus.bg, border: `1px solid ${activityStatus.border}`, color: activityStatus.color, fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: activityStatus.color, boxShadow: activityStatus.label === 'Live' ? `0 0 10px ${activityStatus.color}` : 'none' }} />
              {activityStatus.label}
              <span style={{ color: 'var(--text-secondary, #8b949e)', fontWeight: 600, textTransform: 'none' }}>{activityStatus.sub}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={async () => {
              setShareLoading(true);
              try {
                const data = await apiPost('/api/ea-stats/share', {
                  magic: shareTarget.magic,
                  account_id: shareTarget.account_id,
                  ea_name: s.ea_name || ea.ea_name,
                });
                const url = `${window.location.origin}${data.share_url}`;
                await navigator.clipboard.writeText(url);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 3000);
              } catch (e) {} finally { setShareLoading(false); }
            }} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--border-color, #21262d)', border: '1px solid var(--border-color, #30363d)', cursor: 'pointer', color: shareCopied ? '#00ff88' : 'var(--text-secondary, #8b949e)', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }} disabled={shareLoading}>
              {shareCopied ? <><CheckIcon size={13} /> Copied!</> : <><Share2 size={13} /> Share</>}
            </button>
            <button type="button" onClick={togglePublishedProfile} title={shareTarget.account_id ? 'Publish a connector-verified profile in Explore' : 'Select one account tab before publishing'} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: published ? '#00ff8820' : 'var(--border-color, #21262d)', border: published ? '1px solid #00ff8860' : '1px solid var(--border-color, #30363d)', cursor: publishLoading || !shareTarget.account_id ? 'not-allowed' : 'pointer', color: published ? '#00ff88' : 'var(--text-secondary, #8b949e)', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, opacity: !shareTarget.account_id ? 0.55 : 1 }} disabled={publishLoading || !shareTarget.account_id}>
              {publishLoading ? <><Activity size={13} /> Updating...</> : published ? <><CheckIcon size={13} /> Published</> : <><Globe size={13} /> Publish profile</>}
            </button>
            {publishFeedback && <span role="status" title={publishFeedback} style={{ color: published ? '#00ff88' : 'var(--text-secondary, #8b949e)', fontSize: '9px', maxWidth: '150px', lineHeight: 1.3 }}>{publishFeedback}</span>}
            <button onClick={() => onAIAnalyze && onAIAnalyze(ea)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #a855f7, #3b82f6)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}><Cpu size={14} /> AI Analyze</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #8b949e)' }}><X size={22} /></button>
          </div>
        </div>

        {/* Instance Tabs â€” shown when same EA runs on multiple accounts */}
        {showTabs && (
          <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border-color, #21262d)', padding: '0 24px', background: 'var(--bg-main, #0d1117)', position: 'sticky', top: '65px', zIndex: 9 }}>
            <button onClick={() => setInstanceTab('combined')} style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, border: 'none', borderBottom: instanceTab === 'combined' ? '2px solid #378ADD' : '2px solid transparent', cursor: 'pointer', background: 'transparent', color: instanceTab === 'combined' ? '#378ADD' : 'var(--text-secondary, #8b949e)' }}>
Combined ({instances.length} accounts)
            </button>
            {instances.map(inst => (
              <button key={`${inst.account_id}_${inst.magic}`} onClick={() => setInstanceTab(`${inst.account_id}_${inst.magic}`)} style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, border: 'none', borderBottom: instanceTab === `${inst.account_id}_${inst.magic}` ? '2px solid #378ADD' : '2px solid transparent', cursor: 'pointer', background: 'transparent', color: instanceTab === `${inst.account_id}_${inst.magic}` ? 'var(--text-primary, #e6edf3)' : 'var(--text-secondary, #8b949e)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: inst.is_demo ? '#f59e0b' : '#00ff88' }} />
                {inst.broker ? `${inst.broker.split(' ')[0]} #${inst.account_number}` : `Account #${inst.account_id}`}
                <span style={{ fontSize: '9px', padding: '1px 4px', borderRadius: '3px', background: inst.is_demo ? '#f59e0b20' : '#00ff8820', color: inst.is_demo ? '#f59e0b' : '#00ff88' }}>{inst.is_demo ? 'DEMO' : 'REAL'}</span>
              </button>
            ))}
          </div>
        )}

        <div style={{ padding: '20px 24px' }}>
          {/* Live Trading Readiness Verdict */}
          <LiveReadinessVerdict stats={s} totalClosedTrades={totalClosedTrades} />

          {/* Performance Header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <ModalStat label="Net Profit" value={`$${(s.net_profit||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`(${s.winning_trades||0} wins)`} color={(s.net_profit||0) >= 0 ? '#00ff88' : '#ff4757'} />
            <ModalStat label="Win Rate" value={`${s.win_rate||0}%`} sub={`${s.winning_trades||0}W / ${s.losing_trades||0}L`} color={(s.win_rate||0) >= 50 ? '#00ff88' : '#ff4757'} />
            <ModalStat label="Profit Factor" value={s.profit_factor||0} sub="Risk/Reward" color="var(--text-primary, #e6edf3)" />
            <ModalStat label="Expectancy" value={`$${(s.expectancy||0).toFixed(2)}`} sub="Per trade" color={(s.expectancy||0) >= 0 ? '#00ff88' : '#ff4757'} />
            <ModalStat label="Total Trades" value={s.total_trades||0} sub="Completed" color="var(--text-primary, #e6edf3)" />
            <ModalStat label="% of Balance" value={`${((s.net_profit||0) / Math.max(1, totalBalance || 1) * 100).toFixed(1)}%`} sub="" color={(s.net_profit||0) >= 0 ? '#f59e0b' : '#ff4757'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <ModalStat label="Current Balance" value={`$${(cashflows.current_balance||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub="Live account balance" color="#a855f7" />
            <ModalStat label="Total Deposits" value={`$${(cashflows.total_deposits||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`${cashflows.deposit_count||0} deposits`} color="#00ff88" />
            <ModalStat label="Total Withdrawals" value={`$${(cashflows.total_withdrawals||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`${cashflows.withdrawal_count||0} withdrawals`} color="#ff4757" />
            <ModalStat label="Net Cashflow" value={`$${(cashflows.net_cashflow||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`${cashflows.cashflow_count||0} cashflow records`} color={(cashflows.net_cashflow||0) >= 0 ? '#00ff88' : '#ff4757'} />
          </div>
          {/* Time-Based Comparison */}
          <SectionTitle title="Time-Based Comparison" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <ModalStat label="Days Active" value={`${s.days_active||0}d`} color="#f59e0b" />
            <ModalStat label="Profit/Day" value={`$${(s.profit_per_day||0).toFixed(2)}`} color={(s.profit_per_day||0) >= 0 ? '#00ff88' : '#ff4757'} />
            <ModalStat label="Profit/Month" value={`$${(s.profit_per_month||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} color={(s.profit_per_month||0) >= 0 ? '#00ff88' : '#ff4757'} />
            <ModalStat label="Trades/Day" value={(s.trades_per_day||0).toFixed(2)} color="var(--text-primary, #e6edf3)" />
          </div>
          {/* Monthly Analytics */}
          <EAMonthlyAnalyticsSection data={showTabs && instanceTab === 'combined' ? null : activeDetail.monthly_analytics} />

          {/* Drawdown Analysis */}
          <DrawdownAnalysisSection stats={s} ddChart={ddChart} floatChart={floatChart} />

          {/* Floating Drawdown Records */}
          <FloatingDDRecordsSection magic={ea.magic} accountId={scopedAccountId} combined={!scopedAccountId && showTabs} accounts={accounts} />

          {/* Trading Costs */}
          <p style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><DollarSign size={14} /> Trading Costs</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: (costs.total_swap||0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '10px', marginBottom: '6px' }}>Total Swap</p>
              <p style={{ color: (costs.total_swap||0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '20px', fontWeight: 700, margin: 0 }}>{(costs.total_swap||0) >= 0 ? '+' : '-'}${Math.abs(costs.total_swap||0).toFixed(2)}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>Overnight interest</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#f59e0b', fontSize: '10px', marginBottom: '6px' }}>Total Commission</p>
              <p style={{ color: (costs.total_commission||0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '20px', fontWeight: 700, margin: 0 }}>{(costs.total_commission||0) >= 0 ? '+' : '-'}${Math.abs(costs.total_commission||0).toFixed(2)}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>Broker fees</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#ff4757', fontSize: '10px', marginBottom: '6px' }}>Total Costs</p>
              <p style={{ color: (costs.total_costs||0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '20px', fontWeight: 700, margin: 0 }}>{(costs.total_costs||0) >= 0 ? '+' : '-'}${Math.abs(costs.total_costs||0).toFixed(2)}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>${Math.abs(costs.cost_per_trade||0).toFixed(2)}/trade</p>
            </div>
          </div>

          {/* Win/Loss Streak */}
          <p style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Flame size={14} /> Win/Loss Streak Analysis</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#00ff88', fontSize: '10px', marginBottom: '6px' }}>Current Streak</p>
              <p style={{ color: (s.current_streak||0) >= 0 ? '#00ff88' : '#ff4757', fontSize: '28px', fontWeight: 700, margin: 0 }}>{Math.abs(s.current_streak||0)}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>{(s.current_streak||0) >= 0 ? 'Winning' : 'Losing'} trades</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#00ff88', fontSize: '10px', marginBottom: '6px' }}>Longest Win Streak</p>
              <p style={{ color: '#00ff88', fontSize: '28px', fontWeight: 700, margin: 0 }}>{s.longest_win_streak||0}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>Total: ${((s.longest_win_streak||0) * (s.avg_win||0)).toFixed(2)}</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#ff4757', fontSize: '10px', marginBottom: '6px' }}>Longest Loss Streak</p>
              <p style={{ color: '#ff4757', fontSize: '28px', fontWeight: 700, margin: 0 }}>{s.longest_loss_streak||0}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>Total: -${Math.abs((s.longest_loss_streak||0) * (s.avg_loss||0)).toFixed(2)}</p>
            </div>
          </div>

          {/* Session Performance */}
          <p style={{ color: '#00ff88', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Globe size={14} /> Session Performance</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <SessionBoxPrecomputed name="Asian" flag="Asia" time="19:00-04:00 NY" color="#f59e0b" data={sessionPerf.asian} />
            <SessionBoxPrecomputed name="London" flag="UK" time="03:00-12:00 NY" color="#00ff88" data={sessionPerf.london} />
            <SessionBoxPrecomputed name="New York" flag="US" time="08:00-17:00 NY" color="#a855f7" data={sessionPerf.new_york} />
          </div>

          {/* Performance by Hours */}
          <p style={{ color: '#378ADD', fontSize: '13px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}><Clock size={14} /> Performance by Hour</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginBottom: '10px' }}>Converted to New York time (UTC-5 winter / UTC-4 summer)</p>
          <div style={{ background: '#0d1525', border: '1px solid #1a2540', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(45px, 1fr))', gap: '3px' }}>
              {hourlyPerf.map(({ hour, profit, count }) => {
                const maxProfit = Math.max(...hourlyPerf.map(h => Math.abs(h.profit)), 1);
                const intensity = Math.min(1, Math.abs(profit) / maxProfit);
                const bg = count === 0 ? 'var(--bg-card, #161b22)' : profit >= 0 ? `rgba(0,255,136,${0.15 + intensity * 0.5})` : `rgba(255,71,87,${0.15 + intensity * 0.5})`;
                return (
                  <div key={hour} style={{ background: bg, borderRadius: '6px', padding: '6px 2px', textAlign: 'center', border: '1px solid #21262d30' }} title={`${hour}:00 - ${count} trades, $${profit.toFixed(2)}`}>
                    <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '8px', margin: 0 }}>{hour}h</p>
                    <p style={{ color: count === 0 ? 'var(--text-dim, #484f58)' : profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '9px', fontWeight: 700, margin: '2px 0 0' }}>
                      {count === 0 ? '-' : `${profit >= 0 ? '+' : ''}${Math.abs(profit) >= 100 ? Math.round(profit) : profit.toFixed(0)}`}
                    </p>
                    <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '7px', margin: 0 }}>{count}t</p>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
              <span style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px' }}>Green = profit, Red = loss, Intensity = magnitude</span>
              <span style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px' }}>Best: {(() => { const best = hourlyPerf.reduce((b, h) => h.profit > b.profit ? h : b, { profit: 0, hour: 0 }); return `${best.hour}:00 NY ($${best.profit.toFixed(0)})`; })()}</span>
            </div>
          </div>

          {/* Performance by Day */}
          <p style={{ color: '#a855f7', fontSize: '13px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={14} /> Performance by Day</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginBottom: '10px' }}>Converted to New York time (UTC-5 winter / UTC-4 summer)</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '6px', marginBottom: '24px' }}>
            {dailyPerf.map(({ day, profit, count, win_rate }) => (
              <div key={day} style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '10px', padding: '12px 8px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', fontWeight: 600, margin: '0 0 6px', textTransform: 'uppercase' }}>{day}</p>
                <p style={{ color: count === 0 ? 'var(--text-dim, #484f58)' : profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '16px', fontWeight: 700, margin: '0 0 4px' }}>
                  {count === 0 ? '-' : `${profit >= 0 ? '+' : ''}$${Math.abs(profit).toFixed(0)}`}
                </p>
                <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', margin: '0 0 2px' }}>{count} trades</p>
                <p style={{ color: win_rate >= 60 ? '#00ff88' : win_rate >= 50 ? '#f59e0b' : '#ff4757', fontSize: '9px', margin: 0 }}>{win_rate}% WR</p>
              </div>
            ))}
          </div>

          {/* Equity Curve */}
          <p style={{ color: '#378ADD', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><TrendingUp size={14} /> Equity Curve</p>
          <div style={{ background: '#0d1525', border: '1px solid #1a2540', borderRadius: '8px', padding: '12px', marginBottom: '24px' }}>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={equityCurve}>
                <defs>
                  <linearGradient id="eqCurveModal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#378ADD" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#378ADD" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2540" />
                <XAxis dataKey="x" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
                <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v.toFixed(0)} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px' }} formatter={v => [`$${Number(v).toFixed(2)}`, 'Equity']} />
                <Area type="monotone" dataKey="equity" stroke="#378ADD" fill="url(#eqCurveModal)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Monte Carlo Simulation */}
          {isFree ? (
            <div className="bg-dark-card border border-dark-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-300">Monte Carlo Simulation <ProBadge /></h3>
              </div>
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Lock size={24} className="text-gray-500 mb-2" />
                <p className="text-gray-400 text-sm">This feature is available in the PRO plan</p>
                <p className="text-gray-500 text-xs mt-1">Unlock advanced Monte Carlo analysis with PRO</p>
              </div>
            </div>
          ) : (
            (() => {
              const mc = activeDetail.monte_carlo;
              if (mc && mc.bands && mc.bands.length > 0) {
                return <MonteCarloSection data={mc} />;
              }
              // Client-side fallback: run simple Monte Carlo from available trades
            if (trades.length >= 10) {
              const profits = trades.map(t => t.profit || 0);
              const n = profits.length;
              const numSim = 100;
              const paths = [];
              for (let s = 0; s < numSim; s++) {
                const shuffled = [...profits].sort(() => Math.random() - 0.5);
                let cum = 0, peak = 0, maxDD = 0;
                const path = shuffled.map(p => { cum += p; if (cum > peak) peak = cum; const dd = peak - cum; if (dd > maxDD) maxDD = dd; return cum; });
                paths.push({ path, final: cum, maxDD, maxDDPct: peak > 0 ? maxDD / peak * 100 : 0 });
              }
              // Build bands
              const nPts = Math.min(n, 100);
              const step = n / nPts;
              const bands = Array.from({ length: nPts }, (_, i) => {
                const idx = Math.min(Math.floor(i * step), n - 1);
                const vals = paths.map(p => p.path[idx]).sort((a, b) => a - b);
                const pct = (arr, p) => arr[Math.min(Math.floor(arr.length * p / 100), arr.length - 1)];
                let origCum = 0; for (let j = 0; j <= idx; j++) origCum += profits[j];
                return { x: i + 1, p5: Math.round(pct(vals,5)*100)/100, p25: Math.round(pct(vals,25)*100)/100, p50: Math.round(pct(vals,50)*100)/100, p75: Math.round(pct(vals,75)*100)/100, p95: Math.round(pct(vals,95)*100)/100, original: Math.round(origCum*100)/100 };
              });
              const finals = paths.map(p => p.final).sort((a,b) => a-b);
              const dds = paths.map(p => p.maxDD).sort((a,b) => a-b);
              const ddPcts = paths.map(p => p.maxDDPct).sort((a,b) => a-b);
              const pct = (arr, p) => arr[Math.min(Math.floor(arr.length * p / 100), arr.length - 1)];
              const mcData = {
                bands,
                stats: { simulations: numSim, total_trades: n,
                  final_equity: { p5: Math.round(pct(finals,5)*100)/100, p25: Math.round(pct(finals,25)*100)/100, median: Math.round(pct(finals,50)*100)/100, p75: Math.round(pct(finals,75)*100)/100, p95: Math.round(pct(finals,95)*100)/100 },
                  max_drawdown: { p5: Math.round(pct(dds,5)*100)/100, median: Math.round(pct(dds,50)*100)/100, p95: Math.round(pct(dds,95)*100)/100 },
                  max_drawdown_pct: { p5: Math.round(pct(ddPcts,5)*100)/100, median: Math.round(pct(ddPcts,50)*100)/100, p95: Math.round(pct(ddPcts,95)*100)/100 },
                }
              };
              return <MonteCarloSection data={mcData} />;
            }
            return null;
          })()
          )}

          {/* Monthly Performance Heatmap */}
          <EAMonthlyHeatmapPrecomputed data={monthlyHeatmap.length > 0 ? monthlyHeatmap : (() => {
            const md = {};
            trades.forEach(t => { if (t.close_time) { const d = new Date(t.close_time); const k = `${d.getFullYear()}-${d.getMonth()+1}`; md[k] = (md[k]||0) + (t.profit||0); } });
            return Object.entries(md).map(([k,v]) => { const [y,m] = k.split('-'); return {year:+y, month:+m, profit:Math.round(v*100)/100}; });
          })()} totalBalance={totalBalance} />

          {/* Daily Performance Heatmap */}
          <EADailyHeatmapPrecomputed data={dailyHeatmap.length > 0 ? dailyHeatmap : (() => {
            const dm = {};
            trades.forEach(t => { if (t.close_time) { const k = t.close_time.split('T')[0]; dm[k] = (dm[k]||0) + (t.profit||0); } });
            return Object.entries(dm).map(([k,v]) => ({date:k, profit:Math.round(v*100)/100}));
          })()} totalBalance={totalBalance} />

          {/* Risk Metrics */}
          <p style={{ color: '#a855f7', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Scale size={14} /> Risk Metrics</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '4px' }}>Risk/Reward</p>
              <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '16px', fontWeight: 700, margin: 0 }}>{(s.risk_reward||0).toFixed(2)}</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '4px' }}>Largest Win</p>
              <p style={{ color: '#00ff88', fontSize: '16px', fontWeight: 700, margin: 0 }}>+${(s.largest_win||0).toFixed(2)}</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '4px' }}>Largest Loss</p>
              <p style={{ color: '#ff4757', fontSize: '16px', fontWeight: 700, margin: 0 }}>-${Math.abs(s.largest_loss||0).toFixed(2)}</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '4px' }}>Avg Duration</p>
              <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '16px', fontWeight: 700, margin: 0 }}>{fmtDur(s.avg_duration_seconds)}</p>
            </div>
          </div>

          {/* Avg Win vs Avg Loss */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#00ff88', fontSize: '10px', marginBottom: '4px' }}>Average Win</p>
              <p style={{ color: '#00ff88', fontSize: '22px', fontWeight: 700, margin: 0 }}>+${(s.avg_win||0).toFixed(2)}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>{s.winning_trades||0} winning trades</p>
            </div>
            <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: '8px', padding: '14px' }}>
              <p style={{ color: '#ff4757', fontSize: '10px', marginBottom: '4px' }}>Average Loss</p>
              <p style={{ color: '#ff4757', fontSize: '22px', fontWeight: 700, margin: 0 }}>-${Math.abs(s.avg_loss||0).toFixed(2)}</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>{s.losing_trades||0} losing trades</p>
            </div>
          </div>

          {/* Symbol Breakdown */}
          {(() => {
            const symbols = (activeDetail.symbol_breakdown && activeDetail.symbol_breakdown.length > 0) ? activeDetail.symbol_breakdown : (() => {
              const sm = {};
              trades.forEach(t => { if (t.symbol) { if (!sm[t.symbol]) sm[t.symbol] = {symbol:t.symbol, profit:0, count:0, wins:0}; sm[t.symbol].profit += t.profit||0; sm[t.symbol].count++; if ((t.profit||0)>0) sm[t.symbol].wins++; } });
              return Object.values(sm).map(s => ({...s, win_rate: s.count > 0 ? Math.round(s.wins/s.count*1000)/10 : 0})).sort((a,b) => b.profit - a.profit);
            })();
            if (symbols.length === 0) return null;
            return (
              <>
                <p style={{ color: '#06b6d4', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Coins size={14} /> Symbol Breakdown</p>
                <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', marginBottom: '24px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                    {symbols.map(sym => (
                      <div key={sym.symbol} style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                        <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '11px', fontWeight: 700, margin: '0 0 4px' }}>{sym.symbol}</p>
                        <p style={{ color: sym.profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '14px', fontWeight: 700, margin: '0 0 2px' }}>
                          {sym.profit >= 0 ? '+' : ''}${sym.profit.toFixed(2)}
                        </p>
                        <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', margin: 0 }}>{sym.count} trades | {sym.win_rate}% WR</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Open Trades */}
          {openTrades.length > 0 && (
            <>
              <p style={{ color: '#06b6d4', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Activity size={14} /> Open Trades ({openTrades.length})</p>
              <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', marginBottom: '24px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color, #21262d)' }}>
                      <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Ticket</th>
                      <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Symbol</th>
                      <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                      <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>Lots</th>
                      <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>Profit</th>
                      <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>Pips</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openTrades.map(t => (
                      <tr key={t.ticket} style={{ borderBottom: '1px solid #21262d20' }}>
                        <td style={{ color: 'var(--text-primary, #e6edf3)', padding: '6px 8px' }}>{t.ticket}</td>
                        <td style={{ color: 'var(--text-primary, #e6edf3)', padding: '6px 8px' }}>{t.symbol}</td>
                        <td style={{ color: t.type === 'BUY' ? '#00ff88' : '#ff4757', padding: '6px 8px', fontWeight: 600 }}>{t.type}</td>
                        <td style={{ color: 'var(--text-primary, #e6edf3)', padding: '6px 8px', textAlign: 'right' }}>{t.lots}</td>
                        <td style={{ color: (t.profit||0) >= 0 ? '#00ff88' : '#ff4757', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{(t.profit||0) >= 0 ? '+' : ''}${(t.profit||0).toFixed(2)}</td>
                        <td style={{ color: (t.pips||0) >= 0 ? '#00ff88' : '#ff4757', padding: '6px 8px', textAlign: 'right' }}>{correctPips(t.pips, t.symbol).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Total Closed Trades - Footer */}
          <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px' }}>Total Closed Trades: <strong style={{ color: 'var(--text-primary, #e6edf3)' }}>{totalClosedTrades}</strong></span>
            <span onClick={() => { onClose(); onNavigate('/trades'); }} style={{ color: '#378ADD', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>View full trade history in Trades page -></span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalStat({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</p>
      <p style={{ color, fontSize: '16px', fontWeight: 700, margin: 0 }}>{value}</p>
      {sub && <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', margin: '2px 0 0' }}>{sub}</p>}
    </div>
  );
}

function SectionTitle({ title }) {
  return <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px', fontWeight: 600, marginBottom: '10px' }}>{title}</p>;
}

function LiveCapitalOverview({ eaStats, accounts, totalBalance }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showAllocations, setShowAllocations] = useState(false);
  const [eaFilter, setEaFilter] = useState('all');
  const colors = ['#00ff88','#a855f7','#378ADD','#06b6d4','#ff4757','#ec4899','#f59e0b','#14b8a6'];

  const groupedEAs = useMemo(() => {
    const normalizeN = (name) => {
      if (!name) return '';
      return name.replace(/\[.*?\.(ecn|pc|raw|pro|std|micro|cent|stp)\]/gi, '[SYMBOL]').replace(/\[T\d+\/S\d+\]/gi, '').trim().toLowerCase();
    };
    const groups = {};
    eaStats.forEach(ea => {
      const normName = normalizeN(ea.ea_name);
      let found = null;
      for (const [key, g] of Object.entries(groups)) {
        if (g.magics.some(m => m.magic === ea.magic)) { found = key; break; }
        if (normName && normalizeN(g.ea_name) === normName) { found = key; break; }
      }
      if (found) {
        groups[found].magics.push(ea);
        groups[found].total_profit += ea.net_profit || 0;
        groups[found].total_trades += ea.total_trades || 0;
        groups[found].account_ids.add(ea.account_id);
      } else {
        const name = ea.ea_name || `EA_${ea.magic}`;
        groups[String(ea.magic)] = { ea_name: name, magics: [ea], total_profit: ea.net_profit || 0, total_trades: ea.total_trades || 0, account_ids: new Set([ea.account_id]) };
      }
    });
    return Object.values(groups).map(g => {
      g.ea_name = g.magics.reduce((shortest, m) => (m.ea_name || '').length < (shortest || '').length ? m.ea_name : shortest, g.magics[0].ea_name) || g.ea_name;
      let capital = 0;
      g.account_ids.forEach(aid => {
        const acct = accounts.find(a => a.id === aid);
        if (acct) capital += acct.balance || 0;
      });
      g.capital = capital;
      return g;
    }).sort((a, b) => b.capital - a.capital);
  }, [eaStats, accounts]);

  const activeEAs = groupedEAs.filter(g => g.total_trades > 0);
  const shownEAs = eaFilter === 'active' ? activeEAs : eaFilter === 'none' ? [] : groupedEAs;

  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '12px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Coins size={16} color="#00ff88" />
          <span style={{ color: '#00ff88', fontSize: '14px', fontWeight: 600 }}>Live Capital Overview</span>
          <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px' }}>Real-time allocation across EAs</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => setShowAllocations(true)} style={{ padding: '5px 12px', background: 'var(--border-color, #21262d)', border: '1px solid var(--border-color, #30363d)', borderRadius: '6px', color: 'var(--text-secondary, #8b949e)', fontSize: '11px', cursor: 'pointer' }}>Allocations</button>
          <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px' }}>{activeEAs.length} active / {shownEAs.length} shown</span>
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #8b949e)', fontSize: '12px' }}>{collapsed ? 'Show' : 'Hide'}</button>
        </div>
      </div>

      {!collapsed && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'var(--bg-main, #0d1117)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', fontWeight: 700, marginBottom: '8px' }}>EAs ({shownEAs.length}/{groupedEAs.length})</div>
              <select value={eaFilter} onChange={e => setEaFilter(e.target.value)} style={{ width: '100%', background: 'var(--bg-main, #0d1117)', border: 0, color: 'var(--text-primary, #e6edf3)', fontSize: '12px', colorScheme: 'dark' }}>
                <option value="all">All EAs</option>
                <option value="active">Active EAs</option>
                <option value="none">Hide EAs</option>
              </select>
            </div>
            <div style={{ background: 'var(--bg-main, #0d1117)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', fontWeight: 700, marginBottom: '8px' }}>Total Capital Allocated</div>
              <div style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '22px', fontWeight: 800 }}>${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div style={{ background: 'var(--bg-main, #0d1117)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', fontWeight: 700, marginBottom: '8px' }}>EAs Running</div>
              <div style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '22px', fontWeight: 800 }}>{activeEAs.length} <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px' }}>across {accounts.length} accounts</span></div>
            </div>
          </div>

          {shownEAs.length > 0 && (
            <div style={{ height: '28px', display: 'flex', borderRadius: '6px', overflow: 'hidden', marginBottom: '12px' }}>
              {shownEAs.map((g, i) => {
                const width = totalBalance > 0 ? (g.capital / totalBalance * 100) : 0;
                return <div key={g.ea_name} title={`${g.ea_name}: $${g.capital.toLocaleString()}`} style={{ width: `${Math.max(width, 2)}%`, background: colors[i % colors.length], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {width > 10 && <span style={{ fontSize: '9px', color: 'var(--bg-main, #0d1117)', fontWeight: 700 }}>{g.ea_name}</span>}
                </div>;
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {accounts.map((a, i) => (
              <span key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--text-secondary, #8b949e)' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: i === 0 ? '#378ADD' : '#f59e0b' }} />
                {a.account_number}
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {shownEAs.map((g, i) => {
              const maxC = Math.max(...shownEAs.map(e => e.capital), 1);
              const width = g.capital / maxC * 100;
              const pct = totalBalance > 0 ? (g.capital / totalBalance * 100).toFixed(1) : '0.0';
              return (
                <div key={g.ea_name} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors[i % colors.length] }} />
                  <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '11px', width: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.ea_name}</span>
                  <div style={{ flex: 1, height: '14px', background: 'var(--bg-main, #0d1117)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${width}%`, background: colors[i % colors.length], borderRadius: '3px' }} />
                  </div>
                  <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '11px', fontWeight: 700, width: '70px', textAlign: 'right' }}>${g.capital.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', width: '40px', textAlign: 'right' }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showAllocations && (
        <AllocationPopup accounts={accounts} groupedEAs={groupedEAs} onClose={() => setShowAllocations(false)} />
      )}
    </div>
  );
}
function AllocationPopup({ accounts, groupedEAs, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '16px', width: '95vw', maxWidth: '650px', maxHeight: '80vh', overflowY: 'auto', padding: '24px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '18px', fontWeight: 700, margin: 0 }}>Capital Allocation Settings</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #8b949e)' }}><X size={20} /></button>
        </div>
        <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px', marginBottom: '20px' }}>Select which accounts each EA is running on. This determines how capital is attributed to each EA.</p>

        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: `minmax(80px,1fr) ${accounts.map(() => 'minmax(70px,100px)').join(' ')}`, gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid var(--border-color, #21262d)', overflowX: 'auto' }}>
          <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '12px', fontWeight: 700 }}>EA</span>
          {accounts.map(a => (
            <div key={a.id} style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '11px', fontWeight: 600, margin: 0 }}>{a.account_number}</p>
              <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', margin: 0 }}>${a.balance?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </div>
          ))}
        </div>

        {/* EA rows */}
        {groupedEAs.map(g => (
          <div key={g.ea_name} style={{ display: 'grid', gridTemplateColumns: `minmax(80px,1fr) ${accounts.map(() => 'minmax(70px,100px)').join(' ')}`, gap: '8px', padding: '8px 0', borderBottom: '1px solid #21262d20' }}>
            <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '12px', fontWeight: 500 }}>{g.ea_name}</span>
            {accounts.map(a => {
              const hasEA = g.magics.some(m => m.account_id === a.id);
              return (
                <div key={a.id} style={{ textAlign: 'center' }}>
                  <input type="checkbox" defaultChecked={hasEA} style={{ accentColor: '#378ADD', width: '16px', height: '16px' }} />
                </div>
              );
            })}
          </div>
        ))}

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #ff4757', borderRadius: '6px', color: '#ff4757', fontSize: '12px', cursor: 'pointer' }}>Reset to Auto-Detect</button>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #a855f7, #378ADD)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Allocations</button>
        </div>
      </div>
    </div>
  );
}

function SessionBoxPrecomputed({ name, flag, time, color, data }) {
  const d = data || { profit: 0, count: 0, win_rate: 0, profit_factor: 0 };
  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
        <span style={{ fontSize: '12px' }}>{flag}</span>
        <span style={{ color, fontSize: '12px', fontWeight: 700 }}>{name}</span>
      </div>
      <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', marginBottom: '8px' }}>{time}</p>
      <p style={{ color: d.profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '20px', fontWeight: 700, margin: '0 0 6px' }}>
        {d.profit >= 0 ? '+' : ''}${Math.abs(d.profit).toLocaleString(undefined, {minimumFractionDigits: 2})}
      </p>
      <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: 0 }}>{d.count} trades | {d.win_rate}% WR</p>
      <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', margin: '2px 0 0' }}>PF: {d.profit_factor}</p>
    </div>
  );
}

function DrawdownAnalysisSection({ stats, ddChart, floatChart }) {
  const [ddType, setDdType] = useState('maximal'); // maximal | absolute
  const [chartMode, setChartMode] = useState('DD'); // DD | Float
  const s = stats;

  const maxDD = s.max_drawdown || 0;
  const maxDDPct = s.max_drawdown_pct || 0;
  const avgDD = s.avg_drawdown || 0;

  // Use pre-computed chart data from backend
  const chartData = chartMode === 'DD' ? ddChart : floatChart;

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Header with Maximal/Absolute toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <TrendingDown size={14} style={{ color: '#ff4757' }} />
        <span style={{ color: '#ff4757', fontSize: '13px', fontWeight: 600 }}>Drawdown Analysis</span>
        <div style={{ display: 'flex', background: 'var(--border-color, #21262d)', borderRadius: '4px', overflow: 'hidden' }}>
          <button onClick={() => setDdType('maximal')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: ddType === 'maximal' ? '#378ADD' : 'transparent', color: ddType === 'maximal' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>Maximal</button>
          <button onClick={() => setDdType('absolute')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: ddType === 'absolute' ? '#378ADD' : 'transparent', color: ddType === 'absolute' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>Absolute</button>
        </div>
      </div>

      {/* Subtitle */}
      <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', marginBottom: '12px' }}>
        {ddType === 'maximal' ? 'Peak-to-trough equity decline (trade-by-trade)' : 'Decline below starting equity (trade-by-trade)'}
      </p>

      {/* DD Stats boxes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: '8px', padding: '14px' }}>
          <p style={{ color: '#ff4757', fontSize: '10px', marginBottom: '4px' }}>{ddType === 'maximal' ? 'Max Drawdown' : 'Absolute Drawdown'}</p>
          <p style={{ color: '#ff4757', fontSize: '22px', fontWeight: 700, margin: 0 }}>${maxDD.toFixed(2)}</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px' }}>{maxDDPct.toFixed(2)}% of peak equity</p>
        </div>
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: '8px', padding: '14px' }}>
          <p style={{ color: '#ff4757', fontSize: '10px', marginBottom: '4px' }}>Avg Drawdown</p>
          <p style={{ color: '#ff4757', fontSize: '22px', fontWeight: 700, margin: 0 }}>${avgDD.toFixed(2)}</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px' }}>{maxDD > 0 ? (avgDD / maxDD * 100).toFixed(2) : '0.00'}% of max drawdown</p>
        </div>
      </div>

      {/* DD / Float toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', background: 'var(--border-color, #21262d)', borderRadius: '4px', overflow: 'hidden' }}>
          <button onClick={() => setChartMode('DD')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: chartMode === 'DD' ? '#378ADD' : 'transparent', color: chartMode === 'DD' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>DD</button>
          <button onClick={() => setChartMode('Float')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: chartMode === 'Float' ? '#378ADD' : 'transparent', color: chartMode === 'Float' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>Float</button>
        </div>
        <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px' }}>
          {chartMode === 'DD' ? 'Drawdown - trade-by-trade' : 'Floating P/L - trade-by-trade'}
        </span>
      </div>

      {/* Chart */}
      <div style={{ background: '#0d1525', border: '1px solid #1a2540', borderRadius: '8px', padding: '12px' }}>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="ddModalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartMode === 'DD' ? '#ff4757' : '#00ff88'} stopOpacity={0.3} />
                <stop offset="95%" stopColor={chartMode === 'DD' ? '#ff4757' : '#00ff88'} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a2540" />
            <XAxis dataKey="date" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
            <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} tickFormatter={v => chartMode === 'DD' ? `${v.toFixed(1)}%` : `$${(v/1000).toFixed(0)}K`} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px' }} />
            <Area type="stepAfter" dataKey="value" stroke={chartMode === 'DD' ? '#ff4757' : '#00ff88'} fill="url(#ddModalGrad)" strokeWidth={1.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function fmtDur(seconds) {
  if (!seconds) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function EAMonthlyHeatmapPrecomputed({ data, totalBalance }) {
  if (!data || data.length === 0) return null;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const years = [...new Set(data.map(d => d.year))].sort();
  const lookup = {};
  data.forEach(d => { lookup[`${d.year}-${d.month}`] = d.profit; });

  return (
    <div style={{ marginBottom: '24px' }}>
      <p style={{ color: '#378ADD', fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><TrendingUp size={14} /> Monthly Performance Heatmap</p>
      <div style={{ background: '#0d1525', border: '1px solid #1a2540', borderRadius: '8px', padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '40px repeat(12, 1fr)', gap: '3px', overflowX: 'auto', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px' }}>Year</span>
          {months.map(m => <span key={m} style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textAlign: 'center' }}>{m}</span>)}
          {years.map(year => (
            <React.Fragment key={year}>
              <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '12px', fontWeight: 700 }}>{year}</span>
              {Array.from({ length: 12 }, (_, i) => {
                const profit = lookup[`${year}-${i + 1}`];
                if (profit === undefined) return <span key={i} style={{ textAlign: 'center', color: 'var(--text-dim, #484f58)', fontSize: '10px' }}>-</span>;
                const bg = profit > 0 ? (profit > 5000 ? '#00cc6a' : '#1a7a4a') : '#7a3030';
                return (
                  <div key={i} style={{ background: bg, borderRadius: '4px', padding: '4px 2px', textAlign: 'center' }}>
                    <span style={{ color: '#fff', fontSize: '9px', fontWeight: 700 }}>{profit > 0 ? '+' : ''}{Math.round(profit)}</span>
                    <br/>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '8px' }}>{profit > 0 ? '+' : ''}{(profit / Math.max(totalBalance || 1, 1) * 100).toFixed(1)}%</span>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function EADailyHeatmapPrecomputed({ data, totalBalance }) {
  const [showArchive, setShowArchive] = useState(false);
  const [yearFilter, setYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(null);

  const { dailyMap, months } = useMemo(() => {
    const map = {};
    (data || []).forEach(d => {
      if (!d?.date) return;
      const date = String(d.date).replace(/\./g, '-').split('T')[0].split(' ')[0];
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
      map[date] = (map[date] || 0) + Number(d.profit || 0);
    });

    const monthKeys = [...new Set(Object.keys(map).map(d => d.substring(0, 7)))].sort().reverse();
    const monthRows = monthKeys.map(ym => {
      const [year, month] = ym.split('-').map(Number);
      const daysInMonth = new Date(year, month, 0).getDate();
      let total = 0;
      let tradingDays = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const key = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        if (map[key] !== undefined && map[key] !== 0) {
          total += map[key];
          tradingDays++;
        }
      }
      return { key: ym, year, month, total, tradingDays };
    });

    return { dailyMap: map, months: monthRows };
  }, [data]);

  if (!data || data.length === 0 || months.length === 0) return null;

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const years = [...new Set(months.map(m => m.year))].sort((a, b) => b - a);
  const monthOptions = [...new Set(months
    .filter(m => yearFilter === 'all' || String(m.year) === yearFilter)
    .map(m => m.month))].sort((a, b) => b - a);
  const filteredMonths = months.filter(m =>
    (yearFilter === 'all' || String(m.year) === yearFilter) &&
    (monthFilter === 'all' || String(m.month) === monthFilter)
  );
  const visibleMonths = selectedMonth
    ? months.filter(m => m.key === selectedMonth)
    : (yearFilter !== 'all' || monthFilter !== 'all' ? filteredMonths.slice(0, 6) : months.slice(0, 2));
  const archivedMonths = months.slice(2);

  const selectMonth = (key) => {
    setSelectedMonth(key);
    const row = months.find(m => m.key === key);
    if (row) {
      setYearFilter(String(row.year));
      setMonthFilter(String(row.month));
    }
    setShowArchive(false);
  };

  const resetFilters = () => {
    setSelectedMonth(null);
    setYearFilter('all');
    setMonthFilter('all');
  };

  return (
    <div style={{ marginBottom: '24px', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <p style={{ color: '#378ADD', fontSize: '13px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}><CalendarDays size={14} /> Daily Performance Heatmap</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowArchive(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(55,138,221,0.12)', color: '#7ab8ff', border: '1px solid rgba(55,138,221,0.35)', borderRadius: '6px', padding: '7px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
          >
            <Calendar size={13} /> Previous months
          </button>
          <select value={yearFilter} onChange={(e) => { setYearFilter(e.target.value); setMonthFilter('all'); setSelectedMonth(null); }} style={{ background: 'var(--bg-card, #161b22)', color: 'var(--text-primary, #e6edf3)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '7px 8px', fontSize: '11px' }}>
            <option value="all">All years</option>
            {years.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
          <select value={monthFilter} onChange={(e) => { setMonthFilter(e.target.value); setSelectedMonth(null); }} style={{ background: 'var(--bg-card, #161b22)', color: 'var(--text-primary, #e6edf3)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '7px 8px', fontSize: '11px' }}>
            <option value="all">All months</option>
            {monthOptions.map(month => <option key={month} value={month}>{monthNames[month - 1]}</option>)}
          </select>
          {(selectedMonth || yearFilter !== 'all' || monthFilter !== 'all') && (
            <button onClick={resetFilters} style={{ background: 'transparent', color: 'var(--text-secondary, #8b949e)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '7px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Reset</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(Math.max(visibleMonths.length, 1), 2)}, minmax(0, 1fr))`, gap: '12px' }}>
        {visibleMonths.map(month => (
          <EADailyMonthCalendar key={month.key} monthInfo={month} dailyMap={dailyMap} monthNames={monthNames} totalBalance={totalBalance} />
        ))}
      </div>

      {showArchive && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)' }} onClick={() => setShowArchive(false)}>
          <aside onClick={(e) => e.stopPropagation()} style={{ width: 'min(380px, 92vw)', height: '100%', background: 'var(--bg-card, #161b22)', borderRight: '1px solid var(--border-color, #21262d)', boxShadow: '0 20px 60px rgba(0,0,0,0.45)', padding: '18px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div>
                <div style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '16px', fontWeight: 800 }}>Previous Months</div>
                <div style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px', marginTop: '3px' }}>{months.length} months with trading data</div>
              </div>
              <button onClick={() => setShowArchive(false)} style={{ width: '32px', height: '32px', display: 'grid', placeItems: 'center', background: 'transparent', color: 'var(--text-secondary, #8b949e)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', cursor: 'pointer' }}><X size={16} /></button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              <select value={yearFilter} onChange={(e) => { setYearFilter(e.target.value); setMonthFilter('all'); setSelectedMonth(null); }} style={{ flex: 1, background: 'var(--bg-main, #0d1117)', color: 'var(--text-primary, #e6edf3)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '8px', fontSize: '12px' }}>
                <option value="all">All years</option>
                {years.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
              <select value={monthFilter} onChange={(e) => { setMonthFilter(e.target.value); setSelectedMonth(null); }} style={{ flex: 1, background: 'var(--bg-main, #0d1117)', color: 'var(--text-primary, #e6edf3)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '8px', fontSize: '12px' }}>
                <option value="all">All months</option>
                {monthOptions.map(month => <option key={month} value={month}>{monthNames[month - 1]}</option>)}
              </select>
            </div>

            <div style={{ display: 'grid', gap: '8px' }}>
              {(yearFilter !== 'all' || monthFilter !== 'all' ? filteredMonths : archivedMonths).map(month => (
                <button key={month.key} onClick={() => selectMonth(month.key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: selectedMonth === month.key ? 'rgba(55,138,221,0.18)' : 'var(--bg-main, #0d1117)', border: selectedMonth === month.key ? '1px solid rgba(55,138,221,0.55)' : '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '11px 12px', cursor: 'pointer', textAlign: 'left' }}>
                  <span>
                    <span style={{ display: 'block', color: 'var(--text-primary, #e6edf3)', fontSize: '13px', fontWeight: 800 }}>{monthNames[month.month - 1]} {month.year}</span>
                    <span style={{ display: 'block', color: 'var(--text-secondary, #8b949e)', fontSize: '10px', marginTop: '2px' }}>{month.tradingDays} trading days</span>
                  </span>
                  <span style={{ color: month.total >= 0 ? '#00ff88' : '#ff4757', fontSize: '13px', fontWeight: 800 }}>{month.total >= 0 ? '+' : '-'}${Math.abs(month.total).toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                </button>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function EADailyMonthCalendar({ monthInfo, dailyMap, monthNames }) {
  const { year: y, month: m, total: monthTotal } = monthInfo;
  const firstDay = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();

  return (
    <div style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', gap: '10px' }}>
        <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '13px', fontWeight: 700 }}>{monthNames[m-1]} {y}</span>
        <span style={{ color: monthTotal >= 0 ? '#00ff88' : '#ff4757', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {monthTotal >= 0 ? '+' : '-'}${Math.abs(monthTotal).toLocaleString(undefined,{maximumFractionDigits:0})}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '2px', marginBottom: '2px' }}>
        {['SUN','MON','TUE','WED','THU','FRI','SAT'].map((d, i) => (
          <span key={d} style={{ textAlign: 'center', fontSize: '8px', fontWeight: 600, color: i === 0 || i === 6 ? '#ff4757' : 'var(--text-secondary, #8b949e)' }}>{d}</span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '2px' }}>
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} style={{ aspectRatio: '1' }} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const key = `${y}-${String(m).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const profit = dailyMap[key];
          const has = profit !== undefined && profit !== 0;
          let bg = 'var(--bg-card, #161b22)';
          let border = '1px solid transparent';
          if (has) {
            if (profit > 1000) { bg = 'rgba(0,255,136,0.25)'; border = '1px solid rgba(0,255,136,0.4)'; }
            else if (profit > 0) { bg = 'rgba(0,255,136,0.12)'; border = '1px solid rgba(0,255,136,0.2)'; }
            else if (profit < -1000) { bg = 'rgba(255,71,87,0.3)'; border = '1px solid rgba(255,71,87,0.5)'; }
            else { bg = 'rgba(255,71,87,0.12)'; border = '1px solid rgba(255,71,87,0.2)'; }
          }
          return (
            <div key={day} style={{ aspectRatio: '1', borderRadius: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: bg, border, minHeight: '32px', overflow: 'hidden' }} title={has ? `${monthNames[m-1]} ${day}, ${y}: ${profit >= 0 ? '+' : '-'}$${Math.abs(profit).toFixed(2)}` : ''}>
              <span style={{ fontSize: '8px', color: 'var(--text-secondary, #8b949e)' }}>{day}</span>
              {has && <span style={{ fontSize: '7px', fontWeight: 700, color: profit >= 0 ? '#00ff88' : '#ff4757' }}>{profit >= 0 ? '+' : '-'}{Math.abs(profit) >= 1000 ? `${(Math.abs(profit)/1000).toFixed(0)}k` : Math.round(Math.abs(profit))}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
function MonteCarloSection({ data }) {
  if (!data || !data.bands || !data.stats) return null;
  const { bands, stats: mc } = data;
  if (!mc.final_equity || !mc.max_drawdown || !mc.max_drawdown_pct) return null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <p style={{ color: '#ec4899', fontSize: '13px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}><Shuffle size={14} /> Monte Carlo Simulation</p>
      <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginBottom: '12px' }}>{mc.simulations} simulations | {mc.total_trades} trades shuffled randomly</p>

      {/* Fan Chart */}
      <div style={{ background: '#0d1525', border: '1px solid #1a2540', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={bands}>
            <defs>
              <linearGradient id="mcP95" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00ff88" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#00ff88" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="mcP75" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00ff88" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#00ff88" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="mcP25" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff4757" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#ff4757" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a2540" />
            <XAxis dataKey="x" stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
            <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}K` : v >= -1000 ? v.toFixed(0) : `${(v/1000).toFixed(1)}K`} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', fontSize: '11px' }} formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === 'original' ? 'Actual' : name === 'p95' ? 'Best 5%' : name === 'p75' ? 'Top 25%' : name === 'p50' ? 'Median' : name === 'p25' ? 'Bottom 25%' : 'Worst 5%']} />
            <Area type="monotone" dataKey="p95" stroke="none" fill="url(#mcP95)" />
            <Area type="monotone" dataKey="p75" stroke="none" fill="url(#mcP75)" />
            <Area type="monotone" dataKey="p50" stroke="var(--text-secondary, #8b949e)" strokeWidth={1} strokeDasharray="4 2" fill="none" />
            <Area type="monotone" dataKey="p25" stroke="none" fill="url(#mcP25)" />
            <Area type="monotone" dataKey="p5" stroke="#ff475740" strokeWidth={0.5} fill="none" />
            <Area type="monotone" dataKey="original" stroke="var(--text-primary, #e6edf3)" strokeWidth={2} fill="none" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '8px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'var(--text-secondary, #8b949e)' }}><span style={{ width: '12px', height: '2px', background: 'var(--text-primary, #e6edf3)', display: 'inline-block' }} /> Actual</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'var(--text-secondary, #8b949e)' }}><span style={{ width: '12px', height: '8px', background: 'rgba(0,255,136,0.15)', display: 'inline-block', borderRadius: '2px' }} /> Top 25-95%</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'var(--text-secondary, #8b949e)' }}><span style={{ width: '12px', height: '2px', background: 'var(--text-secondary, #8b949e)', display: 'inline-block', borderStyle: 'dashed' }} /> Median</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'var(--text-secondary, #8b949e)' }}><span style={{ width: '12px', height: '8px', background: 'rgba(255,71,87,0.1)', display: 'inline-block', borderRadius: '2px' }} /> Bottom 5-25%</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
        {/* Final Equity */}
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px' }}>Final Equity Range</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#00ff88', fontSize: '9px' }}>Best 5%</span>
              <span style={{ color: '#00ff88', fontSize: '11px', fontWeight: 700 }}>+${mc.final_equity.p95.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '9px' }}>Median</span>
              <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '11px', fontWeight: 700 }}>{mc.final_equity.median >= 0 ? '+' : ''}${mc.final_equity.median.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#ff4757', fontSize: '9px' }}>Worst 5%</span>
              <span style={{ color: '#ff4757', fontSize: '11px', fontWeight: 700 }}>{mc.final_equity.p5 >= 0 ? '+' : ''}${mc.final_equity.p5.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Max Drawdown */}
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px' }}>Max Drawdown Range</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#00ff88', fontSize: '9px' }}>Best Case</span>
              <span style={{ color: '#00ff88', fontSize: '11px', fontWeight: 700 }}>-${mc.max_drawdown.p5.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '9px' }}>Median</span>
              <span style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 700 }}>-${mc.max_drawdown.median.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#ff4757', fontSize: '9px' }}>Worst 5%</span>
              <span style={{ color: '#ff4757', fontSize: '11px', fontWeight: 700 }}>-${mc.max_drawdown.p95.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Drawdown % */}
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px' }}>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px' }}>Max DD % of Peak</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#00ff88', fontSize: '9px' }}>Best Case</span>
              <span style={{ color: '#00ff88', fontSize: '11px', fontWeight: 700 }}>{mc.max_drawdown_pct.p5.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '9px' }}>Median</span>
              <span style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 700 }}>{mc.max_drawdown_pct.median.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#ff4757', fontSize: '9px' }}>Worst 5%</span>
              <span style={{ color: '#ff4757', fontSize: '11px', fontWeight: 700 }}>{mc.max_drawdown_pct.p95.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LiveReadinessVerdict({ stats, totalClosedTrades }) {
  const s = stats || {};
  const criteria = [];
  let totalScore = 0;
  let maxScore = 0;
  const addCriterion = (name, score, note) => {
    criteria.push({ name, score, max: 10, note });
    totalScore += score;
    maxScore += 10;
  };

  const tradeCount = totalClosedTrades || s.total_trades || 0;
  if (tradeCount >= 200) addCriterion('Sample Size', 10, `${tradeCount} trades - excellent sample size`);
  else if (tradeCount >= 100) addCriterion('Sample Size', 7, `${tradeCount} trades - good sample size`);
  else if (tradeCount >= 50) addCriterion('Sample Size', 4, `${tradeCount} trades - minimum viable, more data needed`);
  else addCriterion('Sample Size', 1, `${tradeCount} trades - insufficient data for reliable analysis`);

  const pf = s.profit_factor || 0;
  if (pf >= 2.0) addCriterion('Profit Factor', 10, `PF ${pf.toFixed(2)} - excellent edge`);
  else if (pf >= 1.5) addCriterion('Profit Factor', 8, `PF ${pf.toFixed(2)} - strong edge`);
  else if (pf >= 1.2) addCriterion('Profit Factor', 5, `PF ${pf.toFixed(2)} - moderate edge`);
  else if (pf >= 1.0) addCriterion('Profit Factor', 2, `PF ${pf.toFixed(2)} - marginal`);
  else addCriterion('Profit Factor', 0, `PF ${pf.toFixed(2)} - losing system`);

  const wr = s.win_rate || 0;
  const exp = s.expectancy || 0;
  if (exp > 0 && wr >= 55) addCriterion('Win Rate & Expectancy', 9, `${wr}% WR, $${exp.toFixed(2)} expectancy - profitable pattern`);
  else if (exp > 0 && wr >= 40) addCriterion('Win Rate & Expectancy', 7, `${wr}% WR, $${exp.toFixed(2)} expectancy - valid strategy`);
  else if (exp > 0) addCriterion('Win Rate & Expectancy', 5, `${wr}% WR, $${exp.toFixed(2)} expectancy - positive but risky`);
  else if (exp === 0) addCriterion('Win Rate & Expectancy', 2, `${wr}% WR, $${exp.toFixed(2)} expectancy - breakeven`);
  else addCriterion('Win Rate & Expectancy', 0, `${wr}% WR, $${exp.toFixed(2)} expectancy - negative edge`);

  const maxDD = s.max_drawdown_pct || 0;
  if (maxDD <= 10) addCriterion('Max Drawdown', 10, `${maxDD.toFixed(1)}% max DD - conservative`);
  else if (maxDD <= 20) addCriterion('Max Drawdown', 8, `${maxDD.toFixed(1)}% max DD - acceptable risk`);
  else if (maxDD <= 35) addCriterion('Max Drawdown', 5, `${maxDD.toFixed(1)}% max DD - moderate risk`);
  else if (maxDD <= 50) addCriterion('Max Drawdown', 2, `${maxDD.toFixed(1)}% max DD - high risk`);
  else addCriterion('Max Drawdown', 0, `${maxDD.toFixed(1)}% max DD - extreme risk`);

  const daysActive = s.days_active || 0;
  const tradesPerDay = s.trades_per_day || 0;
  if (daysActive >= 90 && tradesPerDay >= 0.5) addCriterion('Consistency', 10, `${daysActive}d active, ${tradesPerDay.toFixed(1)} trades/day - proven consistency`);
  else if (daysActive >= 60 && tradesPerDay >= 0.3) addCriterion('Consistency', 7, `${daysActive}d active, ${tradesPerDay.toFixed(1)} trades/day - good record`);
  else if (daysActive >= 30) addCriterion('Consistency', 4, `${daysActive}d active - short record`);
  else addCriterion('Consistency', 1, `${daysActive}d active - too early to judge`);

  const rr = s.risk_reward || 0;
  if (rr >= 2.0) addCriterion('Risk/Reward', 10, `R:R ${rr.toFixed(2)} - excellent risk management`);
  else if (rr >= 1.5) addCriterion('Risk/Reward', 7, `R:R ${rr.toFixed(2)} - good risk management`);
  else if (rr >= 1.0) addCriterion('Risk/Reward', 5, `R:R ${rr.toFixed(2)} - balanced`);
  else if (rr >= 0.5) addCriterion('Risk/Reward', 3, `R:R ${rr.toFixed(2)} - relies on win rate`);
  else addCriterion('Risk/Reward', 1, `R:R ${rr.toFixed(2)} - poor risk management`);

  const pct = maxScore > 0 ? Math.round(totalScore / maxScore * 100) : 0;
  let verdict, verdictColor, verdictBg, verdictDesc;
  if (pct >= 75) {
    verdict = 'READY FOR LIVE'; verdictColor = '#00ff88'; verdictBg = 'rgba(0,255,136,0.08)';
    verdictDesc = 'This EA shows strong metrics across all criteria. Consider deploying with proper risk management.';
  } else if (pct >= 55) {
    verdict = 'CAUTIOUS - SMALL LIVE OK'; verdictColor = '#f59e0b'; verdictBg = 'rgba(245,158,11,0.08)';
    verdictDesc = 'Decent performance but some areas need improvement. Small lot size recommended if going live.';
  } else if (pct >= 35) {
    verdict = 'NEEDS MORE TESTING'; verdictColor = '#f97316'; verdictBg = 'rgba(249,115,22,0.08)';
    verdictDesc = 'Insufficient evidence for live trading. Continue demo testing and gather more data.';
  } else {
    verdict = 'NOT RECOMMENDED'; verdictColor = '#ff4757'; verdictBg = 'rgba(255,71,87,0.08)';
    verdictDesc = 'This EA does not meet minimum criteria for live trading. Review strategy fundamentals.';
  }

  return (
    <div style={{ background: verdictBg, border: `1px solid ${verdictColor}30`, borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px' }}>
        <div>
          <p style={{ color: verdictColor, fontSize: '14px', fontWeight: 700, margin: 0 }}>{verdict}</p>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: '2px 0 0' }}>{verdictDesc}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ color: verdictColor, fontSize: '28px', fontWeight: 700, margin: 0 }}>{pct}%</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', margin: 0 }}>Readiness Score</p>
        </div>
      </div>
      <div style={{ height: '6px', background: 'var(--border-color, #21262d)', borderRadius: '3px', marginBottom: '14px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: verdictColor, borderRadius: '3px' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px 16px' }}>
        {criteria.map(c => (
          <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: c.score >= 7 ? '#00ff88' : c.score >= 4 ? '#f59e0b' : '#ff4757', fontSize: '9px', fontWeight: 700, width: '34px' }}>{c.score}/{c.max}</span>
            <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', flex: 1 }}>{c.note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function FloatingDDRecordsSection({ magic, accountId, combined = false, accounts = [] }) {
  const [ddData, setDdData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!magic) return;
    setDdData(null);
    const accountQuery = accountId ? `?account_id=${accountId}` : '';
    apiGet(`/api/floating-dd/ea/${magic}${accountQuery}`)
      .then(data => {
        if (data && Object.keys(data).length > 0 && Object.values(data).some(v => v && v.dd_amount)) {
          setDdData(data);
        }
      })
      .catch(() => {});
  }, [magic, accountId]);
  if (!ddData) return null;

  const daily = ddData.daily || ddData.daily_record;
  const weekly = ddData.weekly || ddData.weekly_record;
  const monthly = ddData.monthly || ddData.monthly_record;
  const allTime = ddData.all_time || ddData.alltime_record;

  const records = [
    { label: 'Daily Max', data: daily, icon: '' },
    { label: 'Weekly Max', data: weekly, icon: '' },
    { label: 'Monthly Max', data: monthly, icon: '' },
    { label: 'All-time Max', data: allTime, icon: '' },
  ].filter(r => r.data && r.data.dd_amount);

  if (records.length === 0) return null;

  const formatDate = (iso) => {
    if (!iso) return '-';
    const normalized = String(iso).trim().replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3').replace(' ', 'T');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return String(iso);
    return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)}>
        <p style={{ color: '#ff4757', fontSize: '13px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}><AlertTriangle size={14} /> Floating Drawdown Records <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', fontWeight: 500 }}>{combined ? '(combined worst)' : accountId ? `(account #${accounts.find(a => a.id === accountId)?.account_number || accountId})` : ''}</span></p>
        <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px' }}>{collapsed ? 'Show' : 'Hide'}</span>
      </div>

      {!collapsed && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '14px' }}>
            {[
              { label: 'DAILY RECORD', data: daily },
              { label: 'WEEKLY RECORD', data: weekly },
              { label: 'ALL TIME RECORD', data: allTime },
            ].map(({ label, data }) => (
              <div key={label} style={{ background: '#0d1525', border: '1px solid #1a2540', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</p>
                <p style={{ color: '#ff4757', fontSize: '18px', fontWeight: 700, margin: '0 0 2px' }}>
                  {data ? `-$${Math.abs(data.dd_amount).toLocaleString(undefined, {minimumFractionDigits:2})}` : '-'}
                </p>
                <p style={{ color: '#ff4757', fontSize: '10px', opacity: 0.7, margin: 0 }}>
                  {data ? `${data.dd_percent.toFixed(2)}%` : '-'}
                </p>
              </div>
            ))}
          </div>

          {/* Record List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {records.map(({ label, data, icon }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '10px' }}>{icon}</span>
                    <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '12px', fontWeight: 600 }}>{label}</span>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '10px', margin: '3px 0 0 18px' }}>
                    {formatDate(data.recorded_at)} | Period: {formatDate(data.period_start)} | Peak: ${data.peak_equity ? data.peak_equity.toLocaleString(undefined, {minimumFractionDigits:2}) : '0.00'}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: '#ff4757', fontSize: '13px', fontWeight: 700, margin: 0 }}>-${Math.abs(data.dd_amount).toLocaleString(undefined, {minimumFractionDigits:2})}</p>
                  <p style={{ color: '#ff4757', fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>{data.dd_percent.toFixed(2)}%</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}






